import type { Identifier, RaRecord } from "ra-core";
import type { ComponentType } from "react";
import type { FelixHandoffProjection } from "./whatsapp/inbox/felixTypes";

import type {
  COMPANY_CREATED,
  CONTACT_CREATED,
  CONTACT_NOTE_CREATED,
  DEAL_CREATED,
  DEAL_NOTE_CREATED,
} from "./consts";

export type SignUpData = {
  email: string;
  password: string;
  first_name: string;
  last_name: string;
};

export type SubscriberStatus =
  | "subscribed"
  | "welcome_sent"
  | "welcome_failed"
  | "bounced"
  | "complained"
  | "unsubscribed";

export type Subscriber = {
  email: string;
  status: SubscriberStatus;
  source?: string | null;
  locale?: string | null;
  welcome_email_id?: string | null;
  consent_text?: string | null;
  consent_version?: string | null;
  created_at: string;
  updated_at?: string;
} & Pick<RaRecord, "id">;

// Feature cohorts: phone-number roles / feature flags for the WhatsApp bot.
// phone_digits is the wa_id verbatim — E.164 digits, no leading '+'.
// Marketing / acquisition funnel rollup (one row per period × source × campaign).
// Aggregate-only — never per-person — so it is GDPR-safe to surface in the CRM.
// Populated by the rollup job (web analytics ⨝ contacts/echoes ⨝ optional Meta
// spend); see plans/2026-06-19-web-analytics-funnel-into-crm-plan.md.
export type MarketingLeg =
  | "website"
  | "ctwa"
  | "organic"
  | "snap"
  | "link"
  | "launchpad"
  // Web Create (MYC-015): first Echo made on my.echoflicks.com, no WhatsApp.
  | "my_web";

export type MarketingMetric = {
  period: string; // ISO date (YYYY-MM-DD), acquisition-date cohort
  granularity: string; // "day" (week/month reserved for later)
  leg: MarketingLeg;
  source: string; // "website:<token>" | "meta_ctwa" | "organic"
  campaign_token: string; // "" when none (NOT NULL so the upsert key has no null gotcha)
  ad_id: string; // "" when none
  campaign_name?: string | null; // resolved ad/campaign name (ADS-9), if available
  acquisition_group?: "paid" | "organic" | "unknown";
  medium?: string;
  platform?: string;
  surface?: string;
  campaign_id?: string;
  classifier_version?: string;
  classification_confidence?:
    | "deterministic"
    | "declared"
    | "inferred"
    | "legacy_inferred"
    | "unknown";
  web_pageviews: number;
  web_visitors: number;
  wa_cta_clicks: number;
  new_contacts: number;
  echoes_started: number;
  echoes_completed: number;
  purchases: number;
  spend_cents?: number | null; // Meta (nullable)
  impressions?: number | null;
  link_clicks?: number | null;
  short_link_clicks?: number | null;
  conversations_started?: number | null;
  computed_at?: string;
} & Pick<RaRecord, "id">;

export type TrackingLinkExperience = "b2c" | "real_estate";
export type TrackingLinkStatus = "active" | "paused" | "archived";

export type TrackingLink = {
  slug: string;
  public_code: string;
  name: string;
  description?: string | null;
  experience: TrackingLinkExperience;
  message_template: "b2c_default" | "real_estate_default";
  utm_source?: string | null;
  utm_medium?: string | null;
  utm_campaign?: string | null;
  utm_content?: string | null;
  status: TrackingLinkStatus;
  expires_at?: string | null;
  created_by_user_id?: string | null;
  created_at: string;
  updated_at?: string | null;
} & Pick<RaRecord, "id">;

export type TrackingLinkSummary = TrackingLink & {
  created_by_name?: string | null;
  created_by_sales_id?: Identifier | null;
  total_clicks: number;
  human_clicks: number;
  preview_clicks: number;
  new_contacts: number;
  echoes_started: number;
  echoes_completed: number;
  last_clicked_at?: string | null;
  metrics_computed_at?: string | null;
};

// PII-free operational activity by UTC day. Unlike acquisition cohorts, this
// includes explicit link touches from returning and internal/test users so the
// Marketing and Funnel tabs reconcile with the Links page.
export type TrackingLinkDailyMetric = {
  period: string;
  tracking_link_id: Identifier;
  public_code: string;
  name: string;
  short_link_clicks: number;
  new_contacts: number;
  echoes_started: number;
  echoes_completed: number;
  computed_at?: string | null;
} & Pick<RaRecord, "id">;

export type TrackingLinkClick = {
  tracking_link_id: Identifier;
  request_id: string;
  clicked_at: string;
  click_kind: "human" | "preview_bot" | "automation" | "unknown";
  referrer_host?: string | null;
  country_code?: string | null;
  utm_snapshot?: Record<string, string>;
} & Pick<RaRecord, "id">;

export type FeatureCohort = {
  slug: string;
  name: string;
  description?: string | null;
  self_service: boolean;
  created_at?: string;
} & Pick<RaRecord, "id">;

export type FeatureCohortMemberMode = "grant" | "deny";
export type FeatureCohortEnvironment = "all" | "staging" | "prod";
export type FeatureCohortMemberSource = "crm" | "self_service" | "seed";

export type FeatureCohortMember = {
  cohort_slug: string;
  phone_digits: string;
  mode: FeatureCohortMemberMode;
  environment: FeatureCohortEnvironment;
  source: FeatureCohortMemberSource;
  contact_id?: string | null;
  sales_id?: number | null;
  note?: string | null;
  created_at?: string;
} & Pick<RaRecord, "id">;

export type FeatureCohortImplication = {
  parent_slug: string;
  child_slug: string;
} & Pick<RaRecord, "id">;

// Agency tenants + brand guidelines (real-estate B2B). Members are WhatsApp
// numbers (wa_id digits, same canonical form as feature-cohort members);
// portal users are the EXTERNAL logins of the agency-portal app (its own auth
// realm — never Supabase auth users). The brand document shape is validated
// by agencies/brandProfile.ts (shared-vector contract).
export type AgencyStatus = "active" | "paused";

/** exempt = unmetered (pre-self-serve tenants, "Premiere"); standard = metered
 * by the bot's studio wallet: 2 free films, then packs or monthly plans from
 * the signed offer catalog (bot docs/studio-billing.md). RES-501. */
export type AgencyBillingMode = "exempt" | "standard";

/** Who decided `agencies.is_internal` (B4.1). */
export type AgencyInternalSource = "manual" | "auto";

export type Agency = {
  slug: string;
  name: string;
  website_url?: string | null;
  status: AgencyStatus;
  billing_mode: AgencyBillingMode;
  /** An Echoflicks studio (PT-100, B4.1): our test studio or a prospect we
   * set up. Its films count as ours, not the customer's, and its alerts are
   * tagged internal. `not null default false` in the DB; optional here so rows
   * read before 20260924120000_agencies_is_internal is applied count as
   * customers. Never set by the portal. */
  is_internal?: boolean;
  /** Who decided `is_internal` (B4.1): "manual" = a person (Agencies tab,
   * SQL); "auto" = the database (a prospect studio staff set up, turned
   * external on its client's first sign-in); null = never decided. Missing
   * (undefined) before 20260926120000_agency_internal_auto is applied. */
  internal_source?: AgencyInternalSource | null;
  /** When an "auto" prospect studio turned external (B4.1). */
  handed_over_at?: string | null;
  /** Permanent plus-address tag of the agency's hidden staff logins,
   * `<name>+<suffix>@echoflicks.com` (M25-B2/B3). Assigned by the DB when the
   * agency is created; null until provisioning ran, undefined (missing) before
   * 20260925170000_agency_staff_shadow_accounts is applied. */
  staff_alias_suffix?: string | null;
  created_at?: string;
} & Pick<RaRecord, "id">;

export type AgencyMemberSource = "crm" | "portal" | "seed";

export type AgencyMember = {
  agency_id: string;
  phone_digits: string;
  display_name?: string | null;
  source: AgencyMemberSource;
  sales_id?: number | null;
  created_at?: string;
} & Pick<RaRecord, "id">;

export type AgencyPortalUserRole = "owner" | "member";

export type AgencyPortalUser = {
  agency_id: string;
  email: string;
  display_name?: string | null;
  role: AgencyPortalUserRole;
  /** "pending_onboarding" while a self-serve signup has no agency yet. */
  status?: "active" | "pending_onboarding";
  invited_by_sales_id?: number | null;
  created_at?: string;
  last_login_at?: string | null;
  /** Echoflicks staff login inside this tenant (M25-B1): the portal's Team
   * page hides it. Missing before the shadow-accounts migration. */
  is_staff?: boolean;
  /** Set only on logins auto-provisioned for this CRM staff member (M25-B2);
   * revoking their staff access deletes exactly these rows. */
  shadow_of_sales_id?: number | null;
} & Pick<RaRecord, "id">;

export type AgencyBrandProfile = {
  agency_id: string;
  brand: Record<string, unknown>;
  schema_version: number;
  version: number;
  updated_at?: string;
  updated_by?: string | null;
} & Pick<RaRecord, "id">;

export type BrandExtractionStatus =
  | "pending"
  | "succeeded"
  | "partial"
  | "failed";

export type BrandExtraction = {
  agency_id: string;
  url: string;
  final_url?: string | null;
  status: BrandExtractionStatus;
  signals?: Record<string, unknown> | null;
  llm?: Record<string, unknown> | null;
  error?: string | null;
  duration_ms?: number | null;
  requested_by?: string | null;
  created_at?: string;
} & Pick<RaRecord, "id">;

export type SalesFormData = {
  avatar?: string;
  email: string;
  password?: string;
  first_name: string;
  last_name: string;
  administrator: boolean;
  disabled: boolean;
  blog_role?: BlogRole;
};

/** Real-estate blog admin role (BLOG-005): who may draft and who may publish. */
export type BlogRole = "none" | "editor" | "publisher";

export type Sale = {
  first_name: string;
  last_name: string;
  administrator: boolean;
  blog_role?: BlogRole;
  avatar?: RAFile;
  disabled?: boolean;
  user_id: string;

  /**
   * This is a copy of the user's email, to make it easier to handle by react admin
   * DO NOT UPDATE this field directly, it should be updated by the backend
   */
  email: string;

  /**
   * This is used by the fake rest provider to store the password
   * DO NOT USE this field in your code besides the fake rest provider
   * @deprecated
   */
  password?: string;
} & Pick<RaRecord, "id">;

export type Company = {
  name: string;
  logo: RAFile;
  sector: string;
  size: 1 | 10 | 50 | 250 | 500;
  linkedin_url: string;
  website: string;
  phone_number: string;
  address: string;
  zipcode: string;
  city: string;
  state_abbr: string;
  sales_id?: Identifier;
  created_at: string;
  description: string;
  revenue: string;
  tax_identifier: string;
  country: string;
  context_links?: string[];
  nb_contacts?: number;
  nb_deals?: number;
} & Pick<RaRecord, "id">;

export type EmailAndType = {
  email: string;
  type: "Work" | "Home" | "Other";
};

export type PhoneNumberAndType = {
  number: string;
  type: "Work" | "Home" | "Other";
};

export type Contact = {
  first_name: string;
  last_name: string;
  title: string;
  company_id?: Identifier | null;
  email_jsonb: EmailAndType[];
  avatar?: Partial<RAFile>;
  linkedin_url?: string | null;
  first_seen: string;
  last_seen: string;
  has_newsletter: boolean;
  tags: number[];
  gender: string;
  sales_id?: Identifier;
  status: string;
  background: string;
  phone_jsonb: PhoneNumberAndType[];
  nb_tasks?: number;
  company_name?: string;
  whatsapp_user_id?: string | null;
  whatsapp_opt_in_status?: string | null;
  whatsapp_consent_state?: string | null;
  whatsapp_stopped?: boolean | null;
  whatsapp_opted_out_at?: string | null;
  // First-touch ad/website attribution (dashboard projection, set-once)
  acquisition_source?: string | null;
  // Derived acquisition channel ∈ {website,ctwa,organic,snap}, computed by the
  // dashboard-api from acquisition_source — equality-filterable + faceted.
  acquisition_channel?: string | null;
  acquisition_at?: string | null;
  acquisition_detail?: Record<string, unknown> | null;
  acquisition_event_id?: string | null;
  acquisition_schema_version?: number | null;
  acquisition_group?: "paid" | "organic" | "unknown" | null;
  acquisition_medium?: string | null;
  acquisition_platform?: string | null;
  acquisition_surface?: string | null;
  acquisition_campaign_id?: string | null;
  acquisition_marker?: string | null;
  acquisition_confidence?:
    | "deterministic"
    | "declared"
    | "inferred"
    | "legacy_inferred"
    | "unknown"
    | null;
  acquisition_classifier_version?: string | null;
  acquisition_reason?: string | null;
  // "Felix speaks your language" (plan 2026-09-21 §4.5, LANG-401): the bot's
  // per-contact preference projected by the dashboard indexer. null/absent =
  // default English, never asked. Source ∈ echo-language.v1 `sources`.
  preferred_language?: string | null;
  preferred_language_source?: string | null;
  preferred_language_updated_at?: string | null;
  last_inbound_at?: string | null;
  last_outbound_at?: string | null;
  freeform_window_expires_at?: string | null;
  can_send_freeform?: boolean;
  freeform_block_reason?: string | null;
  lifecycle_stage?: string | null;
  use_count?: number | null;
  use_count_bucket?: string | null;
  completed_echo_count?: number | null;
  first_echo_at?: string | null;
  last_echo_at?: string | null;
  first_completed_echo_at?: string | null;
  last_completed_echo_at?: string | null;
  video_count?: number | null;
  video_count_bucket?: string | null;
  /**
   * Live token/credit balance the bot last reported (echo.started
   * balance_before / commerce.tokens_granted balance snapshots, projected by
   * the dashboard indexer, last-write-wins by occurred_at).
   */
  credit_balance?: number | null;
  /** When the credit_balance snapshot was reported by the bot. */
  credit_balance_updated_at?: string | null;
  /** Flat per-echo price (tokens) the bot last reported on echo.started. */
  credit_video_cost?: number | null;
  /** Latest address the contact asked their HD poster emailed to (PII —
   * projected from poster.hd_email.sent; deletion-covered with the contact). */
  poster_email?: string | null;
  poster_email_updated_at?: string | null;
  poster_emails_sent_count?: number | null;
  average_rating?: number | null;
  average_rating_bucket?: string | null;
  rating_count?: number | null;
  rating_bucket?: string | null;
  last_rating?: number | null;
  /** Title of the echo the latest rating belongs to (RSB: same write as last_rating). */
  last_rated_echo_title?: string | null;
  last_rated_session_id?: string | null;
  /** Title of the newest rendered echo, regardless of rating. */
  last_echo_title?: string | null;
  last_echo_session_id?: string | null;
  has_completed_video?: boolean | null;
  last_session_at?: string | null;
  first_session_at?: string | null;
  failed_session_count?: number | null;
  phone_e164?: string | null;
  phone_country_calling_code?: string | null;
  phone_country_iso2?: string | null;
  phone_country_name?: string | null;
  phone_continent_code?: string | null;
  phone_continent_name?: string | null;
  phone_geo_status?: string | null;
  phone_geo_source?: string | null;
  phone_geo_metadata_version?: string | null;
  phone_geo_updated_at?: string | null;
  // Onboarding funnel projection (dashboard-indexer _funnel_projection). Data
  // already flows through mapContact's spread; typed here for the Funnel board.
  funnel_stage?: string | null;
  funnel_stage_rank?: number | null;
  funnel_stage_at?: Record<string, string> | null;
  funnel_cohort_date?: string | null;
  funnel_skill?: string | null;
  funnel_outcome?: string | null;
  funnel_recovered?: boolean | null;
  funnel_image_count?: number | null;
  is_recurring?: boolean | null;
  i2v_cost_total_usd?: number | null;
  i2v_cost_estimated_total_usd?: number | null;
  i2v_cost_provider_reported_total_usd?: number | null;
  i2v_cost_echo_count?: number | null;
  i2v_cost_unknown_echo_count?: number | null;
  i2v_cost_line_item_count?: number | null;
  i2v_cost_unknown_count?: number | null;
  i2v_cost_by_provider?: Record<string, number | null>;
  i2v_cost_by_concrete_provider?: Record<string, number | null>;
  i2v_cost_by_provider_tier?: Record<string, number | null>;
  i2v_cost_by_model?: Record<string, number | null>;
  i2v_cost_by_segment?: Record<string, number | null>;
  i2v_cost_first_at?: string | null;
  i2v_cost_last_at?: string | null;
  i2v_cost_updated_at?: string | null;
} & Pick<RaRecord, "id">;

export type WhatsAppMessagingCapability = {
  contact_id: Identifier;
  sender_key?: string | null;
  whatsapp_user_id?: string | null;
  opt_in_status: "unknown" | "opted_in" | "opted_out" | string;
  last_inbound_at?: string | null;
  last_outbound_at?: string | null;
  freeform_window_expires_at?: string | null;
  can_send_freeform: boolean;
  requires_template: boolean;
  block_reasons: string[];
  safety_buffer_seconds?: number;
};

export type WhatsAppSender = {
  sender_key: string;
  display_name: string;
  public_e164?: string | null;
  template_account_id?: string | null;
  provider: "meta_cloud" | "twilio" | string;
  lifecycle:
    | "pending_verification"
    | "pending_display_name"
    | "ready"
    | "draining"
    | "disabled"
    | string;
  accept_inbound: boolean;
  allow_new_echo: boolean;
  allow_marketing: boolean;
};

export type WhatsAppSenderCatalog = {
  schema_version: "whatsapp-sender-catalog.v1";
  default_sender_key: string;
  senders: WhatsAppSender[];
  config_release_id: string;
  config_digest: string;
  config_schema_version: number;
};

export type SendWhatsAppMessageInput = {
  contact_id: Identifier;
  sender_key: string;
  message_kind: "freeform_text" | "template";
  body?: string;
  preview_url?: boolean;
  template_id?: Identifier;
  template_name?: string;
  template_language?: string;
  template_components?: unknown[];
  template_parameters?: Record<string, string>;
  idempotency_key: string;
  operator_user_id?: Identifier;
  operator_name?: string;
  /** When true the API keeps the open inbox episode open after an operator
   * send instead of auto-resolving it. */
  keep_open?: boolean;
};

export type WhatsAppOutboundMessage = {
  id: Identifier;
  contact_id: Identifier;
  whatsapp_user_id: string;
  sender_key?: string | null;
  sender_provider?: string | null;
  sender_phone_number_id?: string | null;
  operator_user_id?: Identifier | null;
  operator_name?: string | null;
  message_kind: "freeform_text" | "template" | string;
  body?: string | null;
  template_name?: string | null;
  template_language?: string | null;
  provider_message_id?: string | null;
  provider_status: string;
  provider_error_code?: string | null;
  provider_error_title?: string | null;
  provider_error_details?: string | null;
  created_at: string;
  sent_at?: string | null;
  status_updated_at?: string | null;
  failed_at?: string | null;
};

export type SendWhatsAppMessageResult = WhatsAppOutboundMessage;

export type WhatsAppTemplateStatus =
  | "draft"
  | "pending"
  | "approved"
  | "rejected"
  | "paused"
  | "disabled"
  | "in_appeal"
  | "active"
  | string;

export type WhatsAppTemplateParameterField = {
  key: string;
  label: string;
  component: "header" | "body" | "button" | string;
  index: number;
  required?: boolean;
  type?: "text" | "currency" | "date_time" | string;
};

export type WhatsAppTemplate = {
  id: Identifier;
  template_id?: Identifier;
  meta_template_id?: string | null;
  provider_template_id?: string | null;
  waba_id?: string | null;
  provider_waba_id?: string | null;
  sender_environment?: string | null;
  sender_phone_number_id?: string | null;
  library_template_name?: string | null;
  latest_import_id?: Identifier | null;
  name: string;
  language: string;
  category: "UTILITY" | "MARKETING" | "AUTHENTICATION" | string;
  status: WhatsAppTemplateStatus;
  quality_score?: string | null;
  body_text?: string | null;
  header_format?: string | null;
  header_text?: string | null;
  header_handle?: string | null;
  header_media_asset_id?: Identifier | null;
  header_media_url?: string | null;
  header_media_mime_type?: string | null;
  header_media_file_name?: string | null;
  header_example?: string | null;
  body_examples?: string[][] | string[] | null;
  footer_text?: string | null;
  buttons?: unknown[] | null;
  components?: unknown[] | null;
  parameter_schema?: WhatsAppTemplateParameterField[];
  rejection_reason?: string | null;
  paused_reason?: string | null;
  appeal_status?: string | null;
  appeal_notes?: Array<{
    note: string;
    created_at: string;
    operator_user_id?: Identifier;
    operator_name?: string;
  }>;
  send_eligible?: boolean;
  broadcast_eligible?: boolean;
  source?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  submitted_at?: string | null;
  last_synced_at?: string | null;
  sync_status?: string | null;
  fixed_content?: boolean;
  template_status_source?: string | null;
} & Pick<RaRecord, "id">;

export type WhatsAppTemplateLibraryItem = {
  id: Identifier;
  template_id?: Identifier;
  provider_template_id?: string | null;
  library_template_name: string;
  name: string;
  language: string;
  category: "UTILITY" | "MARKETING" | "AUTHENTICATION" | string;
  body_text?: string | null;
  body_examples?: string[][] | string[] | null;
  header_text?: string | null;
  buttons?: unknown[] | null;
  topic?: string | null;
  usecase?: string | null;
  industry?: string[] | string | null;
  fixed_content?: boolean;
  source?: string | null;
  parameter_schema?: WhatsAppTemplateParameterField[];
  raw?: Record<string, unknown>;
} & Pick<RaRecord, "id">;

export type WhatsAppTemplateLibraryFilters = {
  search?: string;
  topic?: string;
  usecase?: string;
  industry?: string;
  language?: string;
  cursor?: string | null;
  limit?: number;
};

export type WhatsAppTemplateLibraryPage = {
  data: WhatsAppTemplateLibraryItem[];
  total?: number;
  next_cursor?: string | null;
  provider?: string | null;
};

export type WhatsAppTemplateLibraryBodyInputs = {
  addContactNumber?: boolean;
  addLearnMoreLink?: boolean;
  addSecurityRecommendation?: boolean;
  addTrackPackageLink?: boolean;
  codeExpirationMinutes?: number;
};

export type WhatsAppTemplateLibraryButtonInput = {
  type?: string;
  phoneNumber?: string;
  url?: Record<string, string>;
  otpType?: string;
  zeroTapTermsAccepted?: boolean;
  supportedApps?: Array<Record<string, string>>;
};

export type WhatsAppTemplateLibraryImportInput = {
  library_template_name: string;
  template_name: string;
  template_language: string;
  template_category: "UTILITY" | "MARKETING" | "AUTHENTICATION" | string;
  library_template_body_inputs?: WhatsAppTemplateLibraryBodyInputs;
  library_template_button_inputs?: WhatsAppTemplateLibraryButtonInput[];
  library_template_snapshot?: Partial<WhatsAppTemplateLibraryItem> &
    Record<string, unknown>;
  operator_user_id?: Identifier;
  operator_name?: string;
};

export type WhatsAppTemplateLibraryImport = {
  id: Identifier;
  import_id?: Identifier;
  template_id?: Identifier | null;
  meta_template_id?: string | null;
  template_name: string;
  template_language: string;
  template_category?: string | null;
  template_status: WhatsAppTemplateStatus;
  template_quality_score?: string | null;
  library_template_name: string;
  library_template_snapshot?: Record<string, unknown> | null;
  library_template_body_inputs?: WhatsAppTemplateLibraryBodyInputs | null;
  library_template_button_inputs?: WhatsAppTemplateLibraryButtonInput[] | null;
  provider?: string | null;
  provider_waba_id?: string | null;
  provider_region?: string | null;
  sync_status?: string | null;
  last_synced_at?: string | null;
  operator_user_id?: Identifier | null;
  operator_name?: string | null;
  operator_identity_status?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
} & Pick<RaRecord, "id">;

export type WhatsAppTemplateLibraryImportResult = {
  data: WhatsAppTemplate;
  import?: WhatsAppTemplateLibraryImport;
};

export type WhatsAppTemplateLibraryImportSyncResult = {
  data: WhatsAppTemplateLibraryImport;
  template?: WhatsAppTemplate | null;
};

export type WhatsAppTemplateInput = {
  id?: Identifier;
  name: string;
  language: string;
  category: "UTILITY" | "MARKETING" | "AUTHENTICATION" | string;
  body_text?: string;
  header_format?: string;
  header_text?: string;
  header_handle?: string;
  header_media_asset_id?: Identifier;
  header_media_url?: string;
  header_example?: string;
  body_examples?: string[][];
  footer_text?: string;
  buttons?: unknown[];
  components?: unknown[];
  allow_category_change?: boolean;
  submit?: boolean;
};

export type WhatsAppTemplateMediaUploadInput = {
  file_name: string;
  mime_type: string;
  data_base64: string;
  operator_user_id?: Identifier;
  operator_name?: string;
};

export type WhatsAppTemplateMediaAsset = {
  id: Identifier;
  asset_id?: Identifier;
  file_name: string;
  mime_type: string;
  size_bytes: number;
  storage_bucket?: string | null;
  storage_path?: string | null;
  provider_media_id?: string | null;
  header_handle?: string | null;
  sample_handle?: string | null;
  preview_url?: string | null;
  target_header_format?: string | null;
  source_file_name?: string | null;
  source_mime_type?: string | null;
  source_storage_path?: string | null;
  render_status?: string | null;
  upload_strategy?: string | null;
  created_at?: string | null;
} & Pick<RaRecord, "id">;

export type DashboardCapabilities = {
  ok?: boolean;
  service?: string;
  environment?: string;
  whatsapp?: {
    phone_number_id_configured?: boolean;
    business_account_id_configured?: boolean;
    access_token_secret_configured?: boolean;
    graph_api_version?: string;
    template_library_provider?: string;
    aws_social_messaging_region?: string;
    aws_social_messaging_waba_id_configured?: boolean;
  };
  features?: Record<string, boolean | number | string | undefined>;
};

// Population counts per cohort bucket across all contacts, used to render "(n)"
// badges on the contact list filter chips so a legitimately-small bucket reads
// as small rather than broken. `facets[field][value] = count`.
export type ContactFilterFacets = {
  facets: Record<string, Record<string, number>>;
  total: number;
  truncated?: boolean;
  scanned?: number;
};

// Cohort vocabulary mirrored from the dashboard indexer rollup (CB-001). These
// are the equality-filterable buckets the backend resolver recognizes; keep them
// in sync with compute_lifecycle_stage and the Echo metric bucket helpers.
export type ContactLifecycleStage =
  | "prospect"
  | "abandoned_photos"
  | "abandoned_story"
  | "abandoned_director"
  | "generating"
  | "failed"
  | "delivered_once"
  | "repeat";

export type ContactVideoCountBucket = "0" | "1" | "2plus";

export type ContactUseCountBucket = "0" | "1" | "2to4" | "5plus";

export type ContactRatingBucket = "promoter" | "passive" | "detractor" | "none";

export type ContactAverageRatingBucket =
  | "champion"
  | "happy"
  | "mixed"
  | "unhappy"
  | "none";

export type ContactMessagingWindowState = "open" | "closed";

export type ContactGeoCode =
  | "AF"
  | "AN"
  | "AS"
  | "EU"
  | "NA"
  | "OC"
  | "SA"
  | "001"
  | "UNKNOWN"
  | string;

// Recognized keys for broadcast cohort resolution. Equality buckets push down to
// the store; recency rides the gsi1 sort key; reachability/window are computed
// live by the resolver. `all` selects every contact; `limit` caps the audience.
export type ContactCohortFilter = {
  all?: boolean;
  limit?: number;
  lifecycle_stage?: ContactLifecycleStage | string;
  video_count_bucket?: ContactVideoCountBucket | string;
  use_count_bucket?: ContactUseCountBucket | string;
  average_rating_bucket?: ContactAverageRatingBucket | string;
  rating_bucket?: ContactRatingBucket | string;
  whatsapp_opt_in_status?: string;
  acquisition_channel?: string;
  /** Bot-side preferred language (lowercase BCP-47 primary subtag). */
  preferred_language?: string;
  has_completed_video?: boolean;
  active_within_days?: number;
  dormant_beyond_days?: number;
  reachable_only?: boolean;
  messaging_window?: ContactMessagingWindowState | string;
  phone_country_calling_code?: string;
  phone_country_iso2?: string;
  phone_continent_code?: ContactGeoCode;
  phone_geo_status?: string;
  geo_continents_any?: ContactGeoCode[];
  geo_continents_none?: ContactGeoCode[];
  geo_countries_any?: string[];
  geo_countries_none?: string[];
  geo_status_any?: string[];
  /** RSB multi-select latest-rating refinement: stars 1-5 the latest rating may hold. */
  last_rating_any?: number[];
  /** RSB: additionally (or alone) admit contacts who have never rated. */
  last_rating_none?: boolean;
};

export type WhatsAppAudienceResolution = {
  scan_complete?: boolean;
  pages_scanned?: number;
  contacts_scanned?: number;
  candidate_count?: number;
  resolved_total?: number;
  deduped_count?: number;
  duplicate_count?: number;
  recipient_limit_reached?: boolean;
  scan_limit_reached?: boolean;
  latency_limit_reached?: boolean;
  limit?: number;
  page_size?: number;
  cursor_backend?: string;
  latency_ms?: number;
};

export type WhatsAppBroadcastPreviewRecipient = {
  id: Identifier;
  contact_id: Identifier;
  contact_name?: string | null;
  whatsapp_user_id?: string | null;
  status: "eligible" | "skipped" | string;
  skip_reasons?: string[];
  template_parameters?: Record<string, string>;
  phone_country_calling_code?: string | null;
  phone_country_iso2?: string | null;
  phone_country_name?: string | null;
  phone_continent_code?: string | null;
  phone_continent_name?: string | null;
  phone_geo_status?: string | null;
};

export type WhatsAppBroadcastPreview = {
  template: WhatsAppTemplate;
  recipients: WhatsAppBroadcastPreviewRecipient[];
  counts: {
    total: number;
    eligible: number;
    skipped: number;
    // Eligible recipients without recorded opt-in, included because the operator
    // ticked "include not-opted-in". Always 0 when that option is off.
    included_without_opt_in?: number;
  };
  geo_counts?: Record<
    string,
    { label?: string; total: number; eligible: number; skipped: number }
  >;
  audience_resolution?: WhatsAppAudienceResolution;
  warnings?: Array<{ code: string; message: string }>;
  skip_reasons: Record<string, number>;
  sampled?: boolean;
};

export type ContactAudienceSelection = {
  mode: "explicit" | "filter";
  resource?: "contacts";
  filter?: Record<string, unknown>;
  sort?: { field?: string; order?: "ASC" | "DESC" | string };
  contact_ids?: Identifier[];
  included_contact_ids?: Identifier[];
  excluded_contact_ids?: Identifier[];
  filter_label?: string | null;
};

export type WhatsAppBroadcastInput = {
  sender_key: string;
  name?: string;
  template_id?: Identifier;
  template_name: string;
  template_language: string;
  audience?: ContactAudienceSelection;
  contact_ids?: Identifier[];
  contact_filter?: ContactCohortFilter;
  header_media_asset_id?: Identifier | null;
  default_template_parameters?: Record<string, string>;
  template_parameters?: Record<string, string>;
  template_parameters_by_contact?: Record<string, Record<string, string>>;
  start_immediately?: boolean;
  scheduled_for?: string;
  timezone?: string;
  audience_source?: string;
  audience_filter_snapshot?: Record<string, unknown>;
  // Include contacts without recorded opt-in (unknown consent). Opted-out
  // contacts are always excluded regardless of this flag.
  include_not_opted_in?: boolean;
  operator_compliance_attestation?: string;
  operator_user_id?: Identifier;
  operator_name?: string;
};

export type WhatsAppBroadcast = {
  id: Identifier;
  broadcast_id?: Identifier;
  name: string;
  sender_key?: string | null;
  sender_provider?: string | null;
  sender_phone_number_id?: string | null;
  template_id?: Identifier;
  template_name: string;
  template_language: string;
  template_category?: string | null;
  status: string;
  audience_status?: string | null;
  send_when_ready?: boolean | null;
  scheduled_for?: string | null;
  timezone?: string | null;
  audience_source?: string | null;
  operator_compliance_attestation?: string | null;
  recipient_count: number;
  eligible_count?: number;
  skipped_count?: number;
  queued_count?: number;
  sending_count?: number;
  accepted_count?: number;
  held_count?: number;
  sent_count?: number;
  delivered_count?: number;
  read_count?: number;
  failed_count?: number;
  cancelled_count?: number;
  inbound_count?: number;
  create_command_count?: number;
  buy_count?: number;
  echo_started_count?: number;
  generation_requested_count?: number;
  final_video_created_count?: number;
  final_video_delivered_count?: number;
  ignored_delivered_unread_count?: number;
  ignored_read_no_action_count?: number;
  ignored_no_conversion_count?: number;
  delivery_rate?: number;
  read_rate?: number;
  create_rate?: number;
  skip_reasons?: Record<string, number>;
  created_at: string;
  updated_at?: string | null;
  completed_at?: string | null;
  created_by_user_id?: Identifier | null;
  created_by_name?: string | null;
  audience_filter_snapshot?: Record<string, unknown> | null;
  audience_resolution_stats?: WhatsAppAudienceResolution | null;
  // Re-hydration inputs for editing an unsent broadcast. `audience_selection` is
  // the live selection the API rebuilds from; `audience_filter_snapshot` above
  // is the frozen record of what was chosen at creation.
  audience_selection?: ContactAudienceSelection | null;
  default_template_parameters?: Record<string, string> | null;
  template_parameters_by_contact?: Record<
    string,
    Record<string, string>
  > | null;
  header_media_asset_id?: Identifier | null;
  header_media_source?: string | null;
  include_not_opted_in?: boolean | null;
  edited_at?: string | null;
  edit_count?: number;
  last_edited_by_user_id?: Identifier | null;
  last_edited_by_name?: string | null;
} & Pick<RaRecord, "id">;

export type WhatsAppBroadcastRecipient = {
  id: Identifier;
  broadcast_id: Identifier;
  contact_id: Identifier;
  contact_name?: string | null;
  whatsapp_user_id?: string | null;
  status: string;
  provider_status?: string | null;
  provider_message_id?: string | null;
  outbound_id?: string | null;
  skip_reasons?: string[];
  provider_error_code?: string | null;
  provider_error_title?: string | null;
  provider_error_details?: string | null;
  sent_at?: string | null;
  accepted_at?: string | null;
  first_sent_at?: string | null;
  first_delivered_at?: string | null;
  first_read_at?: string | null;
  first_failed_at?: string | null;
  first_inbound_after_broadcast_at?: string | null;
  first_create_command_at?: string | null;
  /** Set by the dashboard-indexer once the `/buy` milestone ships (Phase 2). */
  first_buy_command_at?: string | null;
  first_echo_started_at?: string | null;
  first_generation_requested_at?: string | null;
  first_final_video_created_at?: string | null;
  first_final_video_delivered_at?: string | null;
  engagement_state?: string | null;
  ignored_state?: string | null;
  attribution_confidence?: string | null;
  reply_text?: string | null;
  reply_context_message_id?: string | null;
  conversation_deep_link?: string | null;
  pricing_category?: string | null;
  pricing_model?: string | null;
  pricing_type?: string | null;
  billable?: boolean | null;
  status_updated_at?: string | null;
  created_at: string;
  updated_at?: string | null;
  phone_country_calling_code?: string | null;
  phone_country_iso2?: string | null;
  phone_country_name?: string | null;
  phone_continent_code?: string | null;
  phone_continent_name?: string | null;
  phone_geo_status?: string | null;
} & Pick<RaRecord, "id">;

export type WhatsAppBroadcastAnalyticsWarning = {
  code: string;
  message: string;
};

export type WhatsAppBroadcastAnalytics = {
  id: Identifier;
  broadcast_id: Identifier;
  template_name?: string | null;
  template_language?: string | null;
  recipient_count: number;
  eligible_count: number;
  skipped_count: number;
  queued_count: number;
  sending_count: number;
  accepted_count: number;
  held_count: number;
  sent_count: number;
  delivered_count: number;
  read_count: number;
  failed_count: number;
  cancelled_count: number;
  inbound_count: number;
  create_command_count: number;
  buy_count?: number;
  echo_started_count: number;
  generation_requested_count: number;
  final_video_created_count: number;
  final_video_delivered_count: number;
  ignored_delivered_unread_count: number;
  ignored_read_no_action_count: number;
  ignored_no_conversion_count: number;
  error_count?: number;
  delivery_rate?: number;
  read_rate?: number;
  create_rate?: number;
  echo_started_rate?: number;
  final_video_delivered_rate?: number;
  attribution_window_hours?: number;
  total_spend?: number | null;
  cost_per_delivered?: number | null;
  cost_per_reply?: number | null;
  billable_message_count?: number | null;
  currency?: string | null;
  template_clicked_url_button?: number | null;
  template_clicked_quick_reply?: number | null;
  template_analytics_available?: boolean;
  template_analytics_unavailable_reason?: string | null;
  updated_at?: string | null;
  last_recomputed_at?: string | null;
  warnings?: WhatsAppBroadcastAnalyticsWarning[];
} & Pick<RaRecord, "id">;

export type WhatsAppBroadcastTimelineBucket = {
  bucket: string;
  sent?: number;
  delivered?: number;
  read?: number;
  inbound?: number;
  create_command?: number;
  echo_started?: number;
  generation_requested?: number;
  final_video_delivered?: number;
};

/** A button the bot attached to an outbound WhatsApp message. */
export type WhatsAppMessageButton = {
  /** `reply` (quick reply) | `url` (CTA link) | `flow` | `list` | `unknown`. */
  kind: "reply" | "url" | "flow" | "list" | "unknown" | string;
  title: string;
  url?: string | null;
  /** Reply payload the bot receives when the contact taps it. */
  callback_data?: string | null;
};

/** The button or list option a contact tapped (inbound interactive rows). */
export type WhatsAppButtonReply = {
  /** `reply` (bot message button) | `list` (list option) | `template`. */
  kind: "reply" | "list" | "template" | string;
  id?: string | null;
  title?: string | null;
};

export type ContactConversationMessage = {
  source_id?: string;
  contact_id: Identifier;
  whatsapp_user_id: string;
  session_id?: Identifier | null;
  workload_id?: string | null;
  direction: "inbound" | "outbound" | string;
  role: "user" | "assistant" | "operator" | string;
  message_type?: string | null;
  message_kind?: string | null;
  text?: string | null;
  body?: string | null;
  provider_message_id?: string | null;
  provider_status?: string | null;
  provider_error_code?: string | null;
  provider_error_title?: string | null;
  provider_error_details?: string | null;
  raw_payload_s3?: { bucket?: string; key?: string } | null;
  occurred_at: string;
  status_updated_at?: string | null;
  failed_at?: string | null;
  source: string;
  source_confidence?: string | null;
  raw_payload?: Record<string, unknown>;
  /** Bot handling outcome for inbound rows (e.g. `fallback_unknown_command`,
   * `ignored_unsupported_type`, `blocked_rendering`). */
  handling_outcome?: string | null;
  /** "none" | "canned" | "flow" — what kind of reply the bot produced. */
  bot_reply_kind?: string | null;
  flow_step?: string | null;
  /** Meta `unsupported` rows only: album_header | unavailable | unknown. */
  unsupported_kind?: string | null;
  unsupported_code?: string | null;
  unsupported_label?: string | null;
  album_media_count?: number | null;
  sender_phone_number_id?: string | null;
  /** Buttons the bot attached to this outbound message, in display order. */
  buttons?: WhatsAppMessageButton[] | null;
  /** Titles only — what bots recorded before `buttons` existed. */
  button_titles?: string[] | null;
  /** The option the contact tapped, for inbound `interactive`/`button` rows. */
  button_reply?: WhatsAppButtonReply | null;
  /** "web" on a my.echoflicks.com turn (MYC-015): not a WhatsApp message. */
  channel?: string | null;
} & Pick<RaRecord, "id">;

/** One page of a contact's WhatsApp conversation (cursor pagination). */
export type ContactConversationPage = {
  data: ContactConversationMessage[];
  has_more: boolean;
  next_cursor: string | null;
};

export type ContactConversationPageParams = {
  cursor?: string | null;
  limit?: number;
  order?: "ASC" | "DESC";
};

// --- Customer messages inbox -------------------------------------------------

/** Triage states an inbox episode moves through. */
export type InboxState = "needs_reply" | "claimed" | "resolved";

/** `attention_state` as reported per contact — `none` means no open episode. */
export type InboxAttentionState = InboxState | "none";

export type InboxPriority = "high" | "normal" | "low";

/** One contact needing (or having needed) a human reply on WhatsApp. Contact
 * ids equal the WhatsApp user id. Everything but the identity/state is
 * optional so the minimal `{ attention_state: "none" }` shape also fits. */
export type InboxConversation = FelixHandoffProjection & {
  contact_id: Identifier;
  whatsapp_user_id: string;
  attention_state: InboxAttentionState;
  attention_priority?: InboxPriority | string | null;
  attention_since?: string | null;
  attention_last_message_at?: string | null;
  attention_count?: number | null;
  attention_reason?: string | null;
  attention_reason_label?: string | null;
  attention_reasons?: string[] | null;
  attention_message_type?: string | null;
  attention_preview?: string | null;
  attention_flow_step?: string | null;
  // Web Create stall episodes (MYC-015; attention_reason "web_create_stalled",
  // attention_message_type "web_create"): which stage stopped moving, the web
  // creation and the Echo (workload id = Echo id) the customer is waiting on.
  attention_web_stage?: "felix" | "render" | string | null;
  attention_web_creation_id?: string | null;
  attention_workload_id?: string | null;
  attention_bot_reply_kind?: "none" | "canned" | "flow" | string | null;
  attention_claimed_by?: string | null;
  attention_claimed_by_name?: string | null;
  attention_claimed_at?: string | null;
  attention_resolved_at?: string | null;
  attention_resolved_by?: string | null;
  attention_resolved_by_name?: string | null;
  attention_resolved_reason?: string | null;
  sender_phone_number_id?: string | null;
  contact_name?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  phone_e164?: string | null;
  phone_country_iso2?: string | null;
  phone_country_calling_code?: string | null;
  lifecycle_stage?: string | null;
  last_session_status?: string | null;
  last_inbound_at?: string | null;
  last_outbound_at?: string | null;
  freeform_window_expires_at?: string | null;
  whatsapp_opt_in_status?: string | null;
} & Pick<RaRecord, "id">;

export type InboxConversationPage = {
  data: InboxConversation[];
  total: number;
  state: InboxState;
  has_more: boolean;
  next_cursor: string | null;
};

export type ListInboxConversationsParams = {
  state: InboxState;
  cursor?: string | null;
  limit?: number;
};

export type InboxSummary = {
  needs_reply: number;
  claimed: number;
  resolved: number;
  needs_reply_by_priority: Record<InboxPriority, number>;
  oldest_needs_reply_since: string | null;
  generated_at?: string | null;
};

export type InboxTransitionInput = {
  operator_name?: string;
  note?: string;
  /** Release only: end a teammate's claim after the operator confirmed (FHC-011). */
  force?: boolean;
};

export type InboxTransitionResult = {
  data: InboxConversation;
  previous_state?: InboxAttentionState | string | null;
};

/** One (contact, email) pair an HD poster was emailed to — the CRM "Poster
 * Emails" tab. Projected by the dashboard-indexer from poster.hd_email.sent
 * events; stored under the contact partition so GDPR deletion covers it. */
/** One "Like & Save" by a CRM user. Rows are RLS-scoped to the signed-in user,
 * so a plain list of `echo_saves` is always "my saves". */
export type EchoSave = {
  user_id: string;
  echo_id: string;
  created_at: string;
} & Pick<RaRecord, "id">;

export type PosterEmailCapture = {
  email: string;
  contact_id: string;
  whatsapp_user_id: string;
  sent_count: number;
  first_sent_at?: string | null;
  last_sent_at?: string | null;
  last_workload_id?: string | null;
} & Pick<RaRecord, "id">;

export type EchoflicksVideoSession = {
  i2v_provider_summary?:
    | import("./experiments/providerExperimentTypes").I2VProviderSummary
    | null;
  source_id: string;
  contact_id?: Identifier | null;
  whatsapp_user_id: string;
  session_id?: string | null;
  workload_id?: string | null;
  status: string;
  source: string;
  source_confidence: string;
  // "real_estate" when the indexer detected a real-estate echo (drives the RE-specific
  // session page that separates the map clip and the drone shot).
  echo_type?: string | null;
  // Test-echo autoqueue provenance (plan 2026-09-02 §3.6). "test_autoqueue" on a
  // synthetic-but-real admin Echo launched from the debug menu / internal API /
  // CRM Labs; absent on every organic Echo. The two companions are the seed pack
  // and the operator's free-text label. Additive and optional: older projections
  // and non-AWS backends simply never set them.
  echo_origin?: string | null;
  test_pack_id?: string | null;
  test_label?: string | null;
  // Web Create (MYC-015/MYC-090): "web" when the Echo was made on
  // my.echoflicks.com instead of a WhatsApp chat, plus the web creation id. The
  // dashboard indexer stamps both monotonically from any `channel: "web"`
  // event; absent on every WhatsApp Echo. Read via echoflicks/webEcho.ts.
  echo_channel?: string | null;
  web_creation_id?: string | null;
  // PDM-074: safe display projection; notification email and policy stay private.
  echo_surface?: string | null;
  presentation?: {
    brand_id?: string;
    brand_name?: string;
    brand_locale?: string;
    mode?: string;
    sample_book?: boolean;
  } | null;
  // Studio film facts (B6, plan 2026-09-26 §3.3): the bot adds them to the
  // event that opens a Studio film's session and the dashboard indexer copies
  // them onto the SESSION/ECHO rows. The creator is a portal user id (the
  // admin-only CRM resolves it to an e-mail from Supabase); absent on every
  // WhatsApp Echo and on Studio films the backfill has not reached yet.
  studio_echo_id?: string | null;
  studio_agency_id?: string | null;
  studio_created_by?: string | null;
  studio_created_by_staff?: boolean | null;
  studio_listing_group?: string | null;
  studio_revision_index?: number | null;
  studio_parent_echo_id?: string | null;
  studio_format?: string | null;
  // Language the interview, title and narration ran in (plan 2026-09-21
  // §4.11 / LANG-401): the orchestrator's `language.resolved` decision,
  // mirrored SESSION→ECHO by the dashboard indexer. Absent / "en" on every
  // Echo made before the feature and on backends that do not project it.
  content_language?: string | null;
  content_language_source?: string | null;
  // Frozen user-selected occasion contract projected by the AWS indexer.
  // Missing on legacy Echoes; explicit "unknown" means the backfill found no
  // reliable event evidence and is distinct from the selected "other" value.
  occasion_key?: string | null;
  occasion_label?: string | null;
  occasion_taxonomy_version?: string | null;
  occasion_selection_source?: string | null;
  // Property type the bot resolved for a real-estate listing (FPV-012/032),
  // projected onto the ECHO row by the dashboard indexer from the
  // `property.type_resolved` event. Absent on every non-RE echo, on legacy
  // rows and until the projection ships — the CRM falls back to the raw event.
  property_type?: string | null;
  property_type_source?: string | null;
  property_type_confidence?: number | null;
  // Optional privacy-bounded sentence generated by the orchestrator's existing
  // summary call. Render as escaped plain text only.
  operator_synopsis?: string | null;
  operator_synopsis_schema_version?: string | null;
  // Exact ElevenLabs voice selected for this Echo. Projected by the AWS
  // dashboard indexer so Sound can link to an exact, server-side filtered list.
  voice_id?: string | null;
  // Bounded `voice_render.v1` projection (planned/effective voice and, since
  // VAF-12, `accent_fidelity` acoustic receipts). Untyped on purpose: read it
  // only through echoflicks/voiceFidelity.ts, which re-validates every field.
  voice_render?: unknown;
  accent_comparison?: unknown;
  // Marketing-campaign attribution (e.g. "snap" — the Instax QR booth campaign);
  // stamped by the dashboard indexer from the bot's generation.requested event.
  campaign_source?: string | null;
  campaign_token?: string | null;
  attribution_model?: string | null;
  attribution_at?: string | null;
  attribution_message_id?: string | null;
  // User-credit accounting for this echo (dashboard-indexer projection from the
  // bot's echo.started + final_video.delivered events). credits_cost is what
  // the echo actually cost the user in tokens (0 = free mode); credits_charged
  // mirrors the bot's tokens_deducted flag. Absent on pre-projection echoes.
  credits_balance_before?: number | null;
  credits_video_cost?: number | null;
  credits_charged?: boolean | null;
  credits_cost?: number | null;
  started_at?: string | null;
  completed_at?: string | null;
  display_date?: string | null;
  script_s3_key?: string | null;
  script_local_path?: string | null;
  script_url?: string | null;
  script_title?: string | null;
  script_description?: string | null;
  script_style?: string | null;
  script_scene_count?: number | null;
  final_video_s3_key?: string | null;
  final_video_local_path?: string | null;
  final_video_url?: string | null;
  final_video_storage_bucket?: string | null;
  final_video_storage_path?: string | null;
  script_storage_bucket?: string | null;
  script_storage_path?: string | null;
  // Durable echo-state.v1.json manifest pointer (the effective-I2V-prompt source
  // of record). Surfaced by the dashboard-API when the manifest was written;
  // absent on older / manifest-off echoes (the CRM degrades to journey events).
  manifest_storage_bucket?: string | null;
  manifest_storage_path?: string | null;
  manifest_s3_key?: string | null;
  poc_collection?: string | null;
  poc_included?: boolean | null;
  movie_title?: string | null;
  is_narrated?: boolean | null;
  error_message?: string | null;
  raw_metadata?: Record<string, unknown>;
  contact_name?: string | null;
  media_count?: number;
  uploaded_media_count?: number;
  generated_asset_count?: number;
  has_final_video?: boolean;
  rating?: number | null;
  feedback_text?: string | null;
  feedback_at?: string | null;
  // Poster the orchestrator generated for this echo (poster.asset.created),
  // projected by the dashboard indexer onto the echo row. A bucket-relative
  // JPEG key (video-generation/<user>/poster-<user>-<uuid>.jpg) plus its
  // dimensions; absent on echoes that never got a poster or were indexed
  // before the projection shipped (see backfill_poster_projection.py).
  poster_storage_key?: string | null;
  poster_storage_bucket?: string | null;
  poster_width?: number | null;
  poster_height?: number | null;
  poster_byte_size?: number | null;
  poster_mime_type?: string | null;
  poster_generated_at?: string | null;
  // QC verdict projected from the composer's aggregate QCRunReport
  // (qc.report.created event). Observe-only: a "failed" status means QC
  // flagged issues but did NOT block delivery. Absent on pre-feature echoes.
  qc_aggregate_status?: string | null; // skipped|passed|warning|failed|repaired|error
  qc_mode?: string | null; // observe_only|repaired|failed_open|failed_closed
  qc_issue_counts?: Record<string, number> | null;
  qc_layer_status?: Record<string, string> | null;
  qc_top_findings?: EchoQcFinding[] | null;
  qc_report_storage_bucket?: string | null;
  qc_report_storage_path?: string | null;
  // Subject-aware framing projection. Absent on legacy echoes and while the
  // rollout flag is disabled; these fields are diagnostic and never gate the
  // session page itself.
  framing_status?: string | null;
  framing_at_risk?: boolean | null;
  framing_failed_scene_count?: number | null;
  framing_repaired_scene_count?: number | null;
  framing_fallback_scene_count?: number | null;
  framing_tracking_scene_count?: number | null;
  framing_report_storage_bucket?: string | null;
  framing_report_storage_path?: string | null;
  framing_latest_at?: string | null;
  // Privacy-minimized identity projection. The first-class summary consumes
  // only this compact DTO; private evidence remains behind the authenticated
  // inspector API and is never fetched while the Echo detail summary renders.
  identity_summary?: unknown;
  identity_availability?: string | null;
  identity_manifest_status?: string | null;
  // GPT dog census (observe-only pilot). One GPT vision call over the uploaded
  // photos, projected flat by the indexer. Never influences the echo; absent on
  // pre-feature echoes and while the lane is disabled.
  dog_census_status?: string | null; // observed|unavailable
  dog_census_mode?: string | null; // observe
  dog_census_model_id?: string | null; // e.g. "gpt-5.5"
  dog_census_dog_count?: number | null;
  dog_census_count_confidence?: string | null; // high|medium|low
  dog_census_dogs?: Array<{
    breed?: string | null;
    confidence?: string | null; // high|medium|low
    photo_count?: number | null;
  }> | null;
  dog_census_reason?: string | null;
  dog_census_unavailable_reason?: string | null; // timeout|api_error|schema_mismatch|image_loads_failed
  dog_census_image_counts?: {
    analyzed?: number | null;
    available?: number | null;
    failed?: number | null;
  } | null;
  dog_census_observed_at?: string | null;
  dog_census_config_release_id?: string | null;
  i2v_cost_total_usd?: number | null;
  i2v_cost_estimated_total_usd?: number | null;
  i2v_cost_provider_reported_total_usd?: number | null;
  i2v_cost_unknown_count?: number | null;
  i2v_cost_line_item_count?: number | null;
  i2v_cost_billable_attempt_count?: number | null;
  i2v_cost_rejected_attempt_count?: number | null;
  i2v_cost_accepted_segment_count?: number | null;
  i2v_cost_by_provider?: Record<string, number | null>;
  i2v_cost_by_concrete_provider?: Record<string, number | null>;
  i2v_cost_by_provider_tier?: Record<string, number | null>;
  i2v_cost_by_model?: Record<string, number | null>;
  i2v_cost_by_segment?: Record<string, number | null>;
  i2v_cost_by_status?: Record<string, number | null>;
  i2v_cost_currency?: string | null;
  i2v_cost_confidence?: string | null;
  i2v_cost_price_version?: string | null;
  i2v_cost_first_at?: string | null;
  i2v_cost_last_at?: string | null;
  i2v_cost_updated_at?: string | null;
  // Cross-domain echo cost summary (ECHO-COST-008). Sits alongside the legacy
  // i2v_cost_* fields; absent on sessions indexed before the rollout.
  echo_cost_total_usd?: number | null;
  echo_cost_estimated_total_usd?: number | null;
  echo_cost_by_domain?: Record<string, number | null>;
  echo_cost_by_provider?: Record<string, number | null>;
  echo_cost_by_model?: Record<string, number | null>;
  // Billing channel -> USD (google_vertex, google_ai_studio, openai, ...), from
  // the 2026-10-01 ledger contract. Funding (cash vs credits) is decided in
  // costs/fundingSource.ts. Absent on sessions indexed before the rollout.
  echo_cost_by_billing_channel?: Record<string, number | null>;
  echo_cost_line_item_count?: number | null;
  echo_cost_request_count?: number | null;
  echo_cost_unknown_count?: number | null;
  echo_cost_currency?: string | null;
  echo_cost_confidence?: string | null;
  echo_cost_updated_at?: string | null;
  echo_cost_input_tokens?: number | null;
  echo_cost_cached_input_tokens?: number | null;
  echo_cost_uncached_input_tokens?: number | null;
  echo_cost_output_tokens?: number | null;
  echo_cost_reasoning_tokens?: number | null;
  echo_cost_visible_output_tokens?: number | null;
  echo_cost_image_text_input_tokens?: number | null;
  echo_cost_image_input_tokens?: number | null;
  echo_cost_image_output_tokens?: number | null;
  // ECL contract C3 (plans/evidence/2026-10-20-ecl/contracts.md). Every field
  // is optional: a session indexed before ECL has none of them and the Echo
  // page renders exactly as before.
  /** Provider -> sum of `provider_reported_credits` over billable rows. */
  echo_cost_provider_credits_by_provider?: Record<string, number | null>;
  /** USD of the rows the vendor usage rate card priced. */
  echo_cost_priced_by_rate_card_usd?: number | null;
  /** `no_rate` | `no_receipt` | `aborted` -> count of unpriced rows. */
  echo_cost_unknown_by_reason?: Record<string, number | null>;
  /**
   * Render rollup (C3/C4): written only on a parent with derivative renders.
   * `echo_cost_total_usd` keeps its meaning (this session's own rows).
   */
  echo_cost_first_render_usd?: number | null;
  echo_cost_derivatives_usd?: number | null;
  echo_cost_derivatives_count?: number | null;
  echo_cost_derivatives_by_kind?: Record<string, number | null>;
  echo_cost_derivatives_unknown_count?: number | null;
  echo_cost_all_renders_usd?: number | null;
  echo_cost_derivatives_updated_at?: string | null;
  // EMC contract C3.7 (plans/evidence/2026-10-21-emc/contracts.md). Every
  // field is optional: a session indexed before EMC has none of them. Cost
  // classes: production (every domain but these two), messaging (`whatsapp`),
  // acquisition (`marketing`). `echo_cost_total_usd` stays production only
  // (D1). The `echo_cost_acquisition_*` fields reach admins only (C6).
  echo_cost_production_usd?: number | null;
  echo_cost_messaging_usd?: number | null;
  echo_cost_acquisition_usd?: number | null;
  echo_cost_all_in_usd?: number | null;
  /** Every WhatsApp row of the Echo, free and failed ones included. */
  echo_cost_whatsapp_messages?: number | null;
  echo_cost_whatsapp_charged?: number | null;
  echo_cost_whatsapp_free?: number | null;
  /** Meta pricing type (`free_customer_service`, ...) -> message count. */
  echo_cost_whatsapp_free_by_type?: Record<string, number | null>;
  /** C7 purpose -> USD. */
  echo_cost_whatsapp_by_purpose?: Record<string, number | null>;
  /** Native EUR amount of the WhatsApp rows (a decimal string on the wire). */
  echo_cost_whatsapp_eur?: number | null;
  /** `meta_ads` / `meta_whatsapp` -> allocated USD. */
  echo_cost_acquisition_by_pool?: Record<string, number | null>;
  echo_cost_acquisition_eur?: number | null;
  echo_cost_acquisition_status?:
    | "provisional"
    | "final"
    | "none"
    | string
    | null;
  /** The credited touch, written even when the Echo got no allocation. */
  echo_cost_acquisition_attribution?: EchoAcquisitionAttribution | null;
  /** Render rollups (C3.7): messaging of the edits and remakes. */
  echo_cost_derivatives_messaging_usd?: number | null;
  echo_cost_all_renders_messaging_usd?: number | null;
  // HDW contract v1 §4 (plan 2026-10-22 HD unused restorations). Every field
  // is optional: a session indexed before HDW-023 has none of them and the
  // card shows no HD split. Avoided spend is an estimate, never in a total.
  /** Outcome -> USD of the billable HD rows (non-zero outcomes only). */
  echo_cost_image_hd_by_outcome?: Record<string, number | null>;
  /** Outcome -> count of the billable HD rows. */
  echo_cost_image_hd_outcome_counts?: Record<string, number | null>;
  /** Sum of `avoided_estimate_usd` over the gate's not-billed rows. */
  echo_cost_image_hd_avoided_usd?: number | null;
  echo_cost_image_hd_avoided_count?: number | null;
  echo_cost_image_hd_outcome_basis?: ImageHdOutcomeBasis | string | null;
  /**
   * A derivative render (customer edit, re-cut, redelivery, or a CRM remake's
   * ledger-only profile). Its cost is rolled up onto `parent_session_id`.
   */
  is_derivative?: boolean | null;
  derivative_kind?: EchoRenderKind | string | null;
  parent_session_id?: string | null;
  parent_workload_id?: string | null;
  /** C4: a CRM remake's cost-only profile; never an Echo in lists. */
  ledger_only?: boolean | null;
  remake_id?: string | null;
  recovery_mode?: string | null;
  creative_lineage?: {
    schema_version: "creative.lineage.crm.v1";
    intent_digest?: string | null;
    checkpoint_namespace?: string | null;
    checkpoint_version?: number | null;
    script_artifact_digest?: string | null;
    script_schema_version?: string | null;
    music_lineage_digest?: string | null;
    committed_at?: string | null;
    music_artifacts?: Array<{
      type: string;
      order: number;
      artifact_digest: string;
      value: unknown;
    }>;
  } | null;
} & Pick<RaRecord, "id">;

export type EchoflicksSessionCostReconciliation = {
  id: Identifier;
  session_id: Identifier;
  generated_at_utc: string;
  basis: "ledger_rows_recomputed";
  stored_summary: {
    total_usd: number | null;
    line_item_count: number | null;
    unknown_count: number | null;
  };
  recomputed: {
    total_usd: number;
    provider_reported_usd: number;
    local_estimate_usd: number;
    line_item_count: number;
    billable_count: number;
    nonbillable_count: number;
    provider_reported_count: number;
    local_estimate_count: number;
    unknown_count: number;
    unknown_by_domain: Record<string, number>;
  };
  consistency: {
    status: "match" | "mismatch" | "missing_summary";
    delta_usd: number | null;
  };
  openai_vendor_api: {
    basis: "openai_organization_costs_api";
    scope: "organization_wide_unallocated";
    echo_amount_usd: null;
    attribution: "not_available_for_echo";
    archive_status:
      | "available"
      | "pending_or_missing"
      | "not_configured"
      | "not_applicable";
    available_days_utc: string[];
    pending_days_utc: string[];
    missing_days_utc: string[];
  };
  /**
   * ECL contract C5: the four evidence tiers. Absent from an API that
   * predates ECL; the panel then shows the two legacy tiles.
   */
  tiers?: EchoCostEvidenceTiers | null;
  /** C5: the parent's RENDER# rollup against the children's stored totals. */
  derivatives?: EchoCostDerivativeConsistency | null;
  /** C5: same shape as `openai_vendor_api`, prefix `elevenlabs/daily/`. */
  elevenlabs_vendor_api?: EchoCostVendorArchive | null;
  /** EMC C6: the Echo's WhatsApp rows, reconciled against Meta's daily cells. */
  whatsapp?: EchoCostWhatsAppCoverage | null;
  /** EMC C6 (admin only): how each marketing row was allocated. */
  allocation?: EchoCostAllocationLine[] | null;
  /** EMC: `available` once the profile carries `echo_cost_all_in_usd`. */
  all_in_cost_status: "not_available" | "available";
};

/** `whatsapp` block of the cost reconciliation (EMC contract C6). */
export type EchoCostWhatsAppCoverage = {
  rows?: number | null;
  /** Rows stamped `priced_from_provider_daily_cell`. */
  reconciled?: number | null;
  /** Rows still waiting for Meta's daily report. */
  pending?: number | null;
  eur?: number | null;
  usd?: number | null;
};

/** One `allocation` line of the cost reconciliation (EMC contract C6). */
export type EchoCostAllocationLine = {
  pool?: string | null;
  campaign_name?: string | null;
  period?: string | null;
  status?: string | null;
  /** e.g. "1/126". */
  ratio?: string | null;
  usd?: number | null;
};

/** One priced evidence tier: dollars and the rows behind them. */
export type EchoCostEvidenceTier = {
  usd?: number | null;
  count?: number | null;
};

export type EchoCostEvidenceTiers = {
  /** Provider usage, provider credits, GPU receipts or provider tokens. */
  provider_metered?: EchoCostEvidenceTier | null;
  /** Priced at list from what we counted (characters, minutes, calls). */
  our_count?: EchoCostEvidenceTier | null;
  /** `estimated_lower_bound` rows (aborted LLM legs). */
  lower_bound?: EchoCostEvidenceTier | null;
  /** EMC C6: allocated marketing rows (admin only). */
  allocated?: EchoCostEvidenceTier | null;
  unpriced?: {
    count?: number | null;
    by_reason?: Partial<Record<EchoCostUnknownReason, number | null>> | null;
  } | null;
};

export type EchoCostUnknownReason = "no_rate" | "no_receipt" | "aborted";

export type EchoCostDerivativeConsistency = {
  status?: "match" | "mismatch" | "none" | string | null;
  count?: number | null;
  rollup_total_usd?: number | null;
  children_total_usd?: number | null;
};

/** An organization-wide vendor usage archive (OpenAI costs, ElevenLabs usage). */
export type EchoCostVendorArchive = {
  basis?: string | null;
  scope?: string | null;
  echo_amount_usd?: null;
  attribution?: string | null;
  archive_status?:
    | "available"
    | "pending_or_missing"
    | "not_configured"
    | "not_applicable"
    | string
    | null;
  available_days_utc?: string[] | null;
  pending_days_utc?: string[] | null;
  missing_days_utc?: string[] | null;
};

/**
 * Render kinds (ECL contract C4). `original` is the first render (the Echo
 * itself); every other kind is a derivative render rolled up onto its parent.
 */
export type EchoRenderKind =
  | "original"
  | "remake"
  | "storyboard_remake"
  | "operator_recut"
  | "customer_edit"
  | "recut"
  | "redelivery";

/**
 * One `SESSION#{parent}/RENDER#{render_workload_id}` rollup row, served by
 * `GET /video-sessions/{id}/render-costs` (C5), newest `requested_at` first.
 */
export type EchoflicksSessionRenderCost = {
  render_workload_id: string;
  render_kind?: EchoRenderKind | string | null;
  /** remake_id for a CRM remake, else the child workload. */
  render_id?: string | null;
  parent_session_id?: string | null;
  requested_at?: string | null;
  status?: string | null;
  total_usd?: number | null;
  /** The child's own all-renders total when it has renders of its own. */
  descendants_total_usd?: number | null;
  by_domain?: Record<string, number | null>;
  by_billing_channel?: Record<string, number | null>;
  unknown_count?: number | null;
  line_item_count?: number | null;
  updated_at?: string | null;
  /** True for a CRM remake: no Echo page of its own, only a cost ledger. */
  ledger_only?: boolean | null;
  /** Optional: false when the render has no ledger (a pre-ECL remake). */
  cost_recorded?: boolean | null;
} & Pick<RaRecord, "id">;

/** C5 `summary` block of `/render-costs`. */
export type EchoflicksSessionRenderCostSummary = {
  first_render_usd?: number | null;
  derivatives_usd?: number | null;
  all_renders_usd?: number | null;
  derivatives_count?: number | null;
  by_kind?: Record<string, number | null>;
};

/**
 * `echo_cost_acquisition_attribution` (EMC contract C3.7): the last-touch
 * credit of the attribution engine. `paid` is the string "true" or "false"
 * on the wire; the dataProvider turns it into a boolean.
 */
export type EchoAcquisitionAttribution = {
  paid?: boolean | null;
  channel?: string | null;
  source?: string | null;
  campaign_key?: string | null;
  campaign_name?: string | null;
  touch_at?: string | null;
  run_id?: string | null;
};

/**
 * What became of one paid HD restoration (HDW contract v1 §4). The indexer
 * stamps it on each billable `image_hd_upscaling` row; producers never send it.
 */
export type ImageHdConsumptionOutcome =
  | "used_in_film"
  | "bound_not_in_film"
  | "unused"
  | "pending"
  | "unattributed";

/** Where an Echo's HD outcome split came from (HDW contract v1 §4). */
export type ImageHdOutcomeBasis = "events" | "backfill_estimate" | "none";

export type EchoflicksSessionCostLineItem = {
  source_id: string;
  usage_id: string;
  session_id?: Identifier | null;
  contact_id?: Identifier | null;
  whatsapp_user_id?: string | null;
  occurred_at?: string | null;
  /**
   * Cost domain discriminator: "i2v" | "music" | "voice" | "llm" |
   * "title_render" | "music_plan" | ... Absent on legacy (pre-cross-domain)
   * rows, which are always i2v — the dataProvider defaults it.
   */
  cost_domain?: string | null;
  /** Flat string map of usage attribution, e.g. {node, input_tokens, output_tokens}. */
  usage_detail?: Record<string, string> | null;
  segment_type?: string | null;
  scene_id?: string | number | null;
  scene_index?: string | number | null;
  scene_label?: string | null;
  source_scene_type?: string | null;
  provider_id?: string | null;
  concrete_provider_id?: string | null;
  provider_tier?: string | null;
  provider_account?: string | null;
  model_id?: string | null;
  model_version?: string | null;
  provider_mode?: string | null;
  resolution?: string | null;
  aspect_ratio?: string | null;
  requested_duration_seconds?: number | null;
  provider_duration_seconds?: number | null;
  billable_quantity?: number | null;
  billable_unit?: string | null;
  unit_price_usd?: number | null;
  estimated_cost_usd?: number | null;
  provider_reported_cost_usd?: number | null;
  provider_reported_credits?: number | null;
  effective_cost_usd?: number | null;
  currency?: string | null;
  price_source?: string | null;
  price_version?: string | null;
  pricing_effective_date?: string | null;
  task_id?: string | null;
  attempt_number?: number | null;
  max_attempts?: number | null;
  lifecycle_status?: string | null;
  qc_disposition?: string | null;
  final_s3_key?: string | null;
  billable?: boolean | null;
  confidence?: string | null;
  /**
   * The ledger's own disposition, e.g. "estimated_unbilled" for a call that
   * timed out or was cancelled but is likely billed. The indexer also copies
   * it into `confidence` when the row has no confidence of its own.
   */
  disposition?: string | null;
  /**
   * HDW contract v1 §4: the indexer's outcome for a billable HD row. Absent
   * on other domains and on rows indexed before HDW-023.
   */
  consumption_outcome?: ImageHdConsumptionOutcome | string | null;
  source?: string | null;
  raw_metadata?: Record<string, unknown>;
} & Pick<RaRecord, "id">;

export type EchoQcFinding = {
  code: string;
  severity: string; // info | warning | critical
  layer_name?: string | null;
  scene_id?: number | null;
  message: string;
  confidence?: number | null;
};

// A single Echo re-make attempt (Level-1 composer replay). Lives as a list on
// the parent SESSION#{id}/PROFILE row (cross-cloud, equality-only — see
// plans/2026-07-01-echo-remake-resend-plan.md). Generate-first, deliver-on-
// approval: nothing reaches WhatsApp until an admin approves.
export type EchoflicksSessionRemakeStatus =
  | "queued"
  | "rendering"
  | "rendered"
  | "delivered"
  | "skipped_out_of_window"
  | "rejected"
  | "failed"
  // A prepared re-cut whose render submission could not be confirmed
  // (dashboard-api `render_submission_requires_reconciliation`). Not polled:
  // only the render's result, if it ran, changes it.
  | "submission_unknown";

export type EchoflicksSessionRemake = {
  session_id?: Identifier | null;
  new_workload_id?: string | null;
  status: EchoflicksSessionRemakeStatus | string;
  requested_by?: string | null;
  requested_at?: string | null;
  rendered_at?: string | null;
  approved_at?: string | null;
  delivered_at?: string | null;
  preview_bucket?: string | null;
  preview_key?: string | null;
  movie_title?: string | null;
  error?: string | null;
  /** e.g. `rerender`, `prepared_storyboard`, `operator_recut`. */
  recovery_mode?: string | null;
  // ECL contract C5: the remake's render ledger. All optional; an API that
  // predates ECL sends none of them and the card shows no cost badge.
  cost_total_usd?: number | null;
  cost_unknown_count?: number | null;
  cost_line_item_count?: number | null;
  /** False when the remake has no render ledger (requested before ECL). */
  cost_recorded?: boolean | null;
  // Preview playback: mapped from preview_bucket/preview_key so the existing
  // useMediaUrl hook (which signs via /media/sign) can consume them exactly like
  // a final video. Set by the AWS dataProvider's mapRemake().
  final_video_storage_bucket?: string | null;
  final_video_storage_path?: string | null;
} & Pick<RaRecord, "id">;

// Terminal statuses no longer change — used to stop polling and to disable the
// Remake button only while an attempt is still in flight.
export const ECHO_REMAKE_TERMINAL_STATUSES: EchoflicksSessionRemakeStatus[] = [
  "delivered",
  "skipped_out_of_window",
  "rejected",
  "failed",
];

export const ECHO_REMAKE_IN_FLIGHT_STATUSES: EchoflicksSessionRemakeStatus[] = [
  "queued",
  "rendering",
];

// ---------------------------------------------------------------------------
// Test-echo autoqueue (plan 2026-09-02-test-echo-autoqueue, TEQ-012)
//
// A stored image pack + story + scripted answers that the bot can replay through
// the real /create flow for an admin WhatsApp number. The CRM only ever reads
// these; packs are created by the CLI / bot API.
// ---------------------------------------------------------------------------

export type TestEchoPack = {
  pack_id: string;
  name?: string | null;
  occasion_callback_id?: string | null;
  occasion_key?: string | null;
  media_count?: number | null;
  image_count?: number | null;
  video_count?: number | null;
  story?: string | null;
  answers_count?: number | null;
  updated_at?: string | null;
  source_workload_id?: string | null;
};

/** "full" = the operator watches Felix's questions scroll by on WhatsApp;
 *  "final_only" = quiet mode, only the finished video + poster are delivered. */
export type TestEchoNotifyMode = "full" | "final_only";

export type TestEchoLaunchRequest = {
  user_id: string;
  pack_id: string;
  notify: TestEchoNotifyMode;
  label?: string;
  force_clear_feedback_gate?: boolean;
  /**
   * Which WhatsApp sender carries this number's conversation. Only needed
   * where more than one sender accepts inbound (customer production runs two),
   * in which case the bot refuses with `route_ambiguous` and lists the keys.
   */
  sender_key?: string;
};

/** The 202 body of POST /launches. */
export type TestEchoLaunch = {
  workload_id: string;
  session_id?: string | null;
  user_id?: string | null;
  pack_id?: string | null;
  status?: string | null;
  crm_url?: string | null;
  whatsapp_window?: {
    last_inbound_at?: string | null;
    within_24h?: boolean | null;
  } | null;
  policy?: {
    config_release_id?: string | null;
    remaining_today?: number | null;
  } | null;
  cost_hint_usd?: string | null;
};

export type TestEchoLaunchStatusValue =
  | "interviewing"
  | "generating"
  | "delivered"
  | "failed"
  | "unknown";

/** The body of GET /launches/{workload_id}. */
export type TestEchoLaunchStatus = {
  workload_id: string;
  status: TestEchoLaunchStatusValue | string;
  step?: string | null;
  answers_sent?: number | null;
  last_prompt_kind?: string | null;
  pack_id?: string | null;
  label?: string | null;
  launched_via?: string | null;
  launched_at?: string | null;
};

/** Statuses that are still moving, so the card keeps polling and Launch stays
 * disabled for that user. Everything else is terminal. */
export const TEST_ECHO_IN_FLIGHT_STATUSES: TestEchoLaunchStatusValue[] = [
  "interviewing",
  "generating",
];

// The compact ExtractionRecord the WhatsApp bot publishes for every listing
// ingest attempt (kind === "ingest_extraction" media rows). Powers the
// "Listing Ingest" panel and the LLM-vs-recipe comparison.
export type EchoflicksListingIngest = {
  ingest_id?: string;
  listing_url?: string;
  canonical_url?: string;
  domain?: string;
  outcome?: string; // success | qc_failed | blocked | not_a_listing | fetch_error
  extractor?: string | null; // tier0 | wpresidence | llm:<model> | recipe:<domain>@v<n>
  blocked_reason?: string | null;
  failure_reason?: string | null;
  tiers_attempted?: Array<{
    tier?: string;
    ms?: number;
    produced?: boolean;
    fields?: string[];
    error?: string;
  }>;
  fields?: Record<string, unknown>;
  grounding?: Record<string, boolean | null>;
  images?: {
    candidates?: number;
    downloaded?: number;
    accepted?: number;
    rejected?: Array<{ url_tail?: string; reason?: string }>;
    accepted_keys?: string[];
    transform_applied?: boolean;
    low_res?: number;
    /** Per accepted photo, in listing order: the listing's own alt text and
     * source filename (FPR-015 photo-hints sidecar). Absent on older ingests. */
    accepted_hints?: Array<{
      index?: number;
      alt?: string | null;
      source_name?: string | null;
    }>;
  };
  inventory?: {
    discovered?: number;
    unique?: number;
    measurable?: number;
    usable_property_photos?: number;
    rejected?: number;
    uploaded?: number;
    render_selected?: number;
    completeness?:
      | "complete"
      | "incomplete"
      | "source_limited"
      | "unknown"
      | string;
    expected_source_count?: number | null;
    reasons?: string[];
  };
  workflow?: {
    template_id?: string;
    version?: number;
    digest?: string;
    status?: string;
  } | null;
  repair?: {
    attempted?: boolean;
    attempt?: number;
    classification?: string;
    outcome?: string;
    diagnostic_id?: string | null;
  } | null;
  qc?: { passed?: boolean; failures?: string[]; warnings?: string[] };
  timings_ms?: Record<string, number>;
  needs_render?: boolean;
  follow_url_used?: boolean;
  shadow?: {
    extractor?: string;
    fields?: Record<string, unknown> | null;
    diff_fields?: string[];
  } | null;
  html_snapshot?: { bucket?: string; key?: string } | null;
  /** Floor-plan read (FPR-014, `floor_plan.v1`). Absent on ingests that
   * predate the lane or where discovery found no plan. */
  floor_plan?: EchoflicksFloorPlan | null;
  /** Building profile (BLD, `building_profile.v1`). Not on the record yet:
   * read when the bot adds it to `ExtractionRecord.to_payload`. */
  building_profile?: EchoflicksBuildingProfile | null;
};

/**
 * The property type the bot derived for a listing (FPV-012). Arrives on
 * `floor_plan.v2` blocks, on the `director.skill_room_map` payload and as its
 * own `property.type_resolved` dashboard event. Every field optional: v1
 * blocks and pre-feature echoes carry none of it.
 */
export type EchoflicksPropertyType = {
  /** apartment | penthouse | duplex_apartment | studio | loft | house | villa
   * | townhouse | semi_detached | country_house | plot | commercial | other. */
  value?: string | null;
  /** e.g. "listing_text", "plan", "listing_text+plan". */
  source?: string | null;
  confidence?: number | null;
  evidence?: string[];
  /** True when plan signals (levels, site plan, lift) contributed. */
  floor_plan?: boolean | null;
};

/**
 * One level of a `floor_plan.v2` plan. A one-sheet duplex has two levels on
 * page 1 separated by `region`; a multi-page plan has one level per page.
 */
export type EchoflicksFloorPlanLevel = {
  level_id?: string | null;
  label?: string | null;
  /** 0 = entrance level, negatives below (−1 = garden/basement). */
  ordinal?: number | null;
  page?: number | null;
  region?: {
    x0?: number | null;
    y0?: number | null;
    x1?: number | null;
    y1?: number | null;
  } | null;
  // --- BLD level model v2 (contract §2.1), all optional -------------------
  /** Building the level belongs to ("B0" = main house when absent). */
  building_id?: string | null;
  /** Floor number of the building, ground = 0 (a 4th-floor flat: 4). */
  storey?: number | null;
  /** basement | lower_ground | ground | upper | attic | mezzanine | roof | split. */
  kind?: string | null;
  /** Float sort key (a mezzanine sits at 0.5); `ordinal` stays an integer. */
  ordinal_sort?: number | null;
  ordinal_source?: "label" | "number" | "page_order" | string | null;
  room_count?: number | null;
  /** "inferred" for a level the plan does not show (orchestrator, BLD). */
  source?: "plan" | "inferred" | string | null;
};

/** A physically separate building on the plot (`floor_plan.v2`, BLD). */
export type EchoflicksFloorPlanBuilding = {
  building_id?: string | null;
  label?: string | null;
  /** main | garage | annex | outbuilding | other. */
  kind?: string | null;
};

/** One plan image or PDF the bot found for the plan set (BLD contract §2.1). */
export type EchoflicksFloorPlanSource = {
  source_index?: number | null;
  url?: string | null;
  kind?: "image" | "pdf" | string | null;
  /** portal_array | dom_container | lightbox_group | cta | pdf_anchor |
   * brochure | gallery_classifier | extractor_exclusion | upload. */
  found_by?: string[];
  status?: "read" | "dropped" | string | null;
  reason?: string | null;
  pages?: number[];
};

/** A coverage gap (closed code set, BLD contract §2.1); a bare code on the
 * building profile, an object on the floor plan. */
export type EchoflicksCoverageGap =
  | string
  | {
      code?: string | null;
      hard?: boolean | null;
      evidence?: string[];
    };

/**
 * Whether the plan set covers every level the building has (BLD coverage
 * detector). Extends today's `{expected_pages, read_page_ids}`; every new
 * field is optional and absent on pre-BLD blocks.
 */
export type EchoflicksPlanCoverage = {
  expected_pages?: number | null;
  read_page_ids?: number[];
  candidates?: Array<{
    url?: string | null;
    status?: "read" | "dropped" | "unread" | string | null;
    reason?: string | null;
  }>;
  status?: "complete" | "partial" | "unknown" | string | null;
  gaps?: EchoflicksCoverageGap[];
};

type BuildingProfileScored = {
  value?: string | null;
  confidence?: number | null;
};

/**
 * `building_profile.v1` (BLD contract §2.2): the bot's evidence-weighted
 * answer for structure type, storeys, unit floor and level set. The room-map
 * event carries a subset (structure, subtype, label, storeys, unit_floor,
 * band, coverage); a full block adds evidence and conflicts. All optional.
 */
export type EchoflicksBuildingProfile = {
  version?: string | null;
  authority?: "observe" | "on" | string | null;
  structure?: BuildingProfileScored | null;
  subtype?: BuildingProfileScored | null;
  label?: {
    value?: string | null;
    locale_label?: string | null;
    confidence?: number | null;
  } | null;
  levels_declared?: BuildingProfileScored | null;
  storeys?: {
    value?: number | null;
    above_ground?: number | null;
    below_ground?: number | null;
    confidence?: number | null;
  } | null;
  storeys_total?: { value?: number | null; confidence?: number | null } | null;
  entry_level?: { ordinal?: number | null; source?: string | null } | null;
  unit_floor?: { value?: number | null; source?: string | null } | null;
  levels_expected?: Array<{ kind?: string | null; ordinal?: number | null }>;
  outbuildings?: Array<{
    kind?: string | null;
    source?: string | null;
    evidence?: string | null;
  }>;
  bedrooms?: {
    value?: number | null;
    source?: string | null;
    upper?: number | null;
  } | null;
  evidence?: Array<{
    fact?: string | null;
    value?: string | number | boolean | null;
    source?: string | null;
    quote?: string | null;
    llr?: number | null;
    weight?: number | null;
  }>;
  conflicts?: Array<{
    fact?: string | null;
    values?: Array<string | number>;
    sources?: string[];
    note?: string | null;
  }>;
  coverage?: EchoflicksPlanCoverage | null;
  property_type_compat?: string | null;
  band?: "accept" | "review" | "reject" | string | null;
  lexicon_version?: string | null;
  policy_digest?: string | null;
};

/** A door, wall gap, stair or lift between two rooms (`floor_plan.v2`). */
export type EchoflicksFloorPlanOpening = {
  opening_id?: string | null;
  from?: string | null;
  to?: string | null;
  /** door | double_door | sliding | french_door | opening | stairs | lift. */
  kind?: string | null;
  position?: { page?: number; x?: number; y?: number } | null;
};

/** One room the floor-plan reader found on the plan. */
export type EchoflicksFloorPlanRoom = {
  room_id?: string | null;
  label_raw?: string | null;
  name?: string | null;
  category?: string | null;
  floor?: string | null;
  /** Level this room sits on (`floor_plan.v2`); absent on v1 blocks. */
  level_id?: string | null;
  area_m2?: number | null;
  /** True when the m² figure was read off the plan (not inferred). */
  area_grounded?: boolean | null;
  position?: { page?: number; x?: number; y?: number } | null;
  features?: string[];
  opens_to?: string[];
  ensuite_of?: string | null;
  service_room?: boolean | null;
  confidence?: number | null;
  /** "inferred" for a placeholder room on a level the plan does not show. */
  source?: "plan" | "inferred" | string | null;
};

export type EchoflicksFloorPlanFeature = {
  feature_id?: string | null;
  kind?: string | null;
  label_raw?: string | null;
  area_m2?: number | null;
  floor?: string | null;
  /** Level the feature sits on (`floor_plan.v2`); absent on v1 blocks. */
  level_id?: string | null;
  position?: { page?: number; x?: number; y?: number } | null;
  adjacent_rooms?: string[];
};

/**
 * Plan-vs-listing (or mapped-vs-listing-vs-plan) room counts, keyed by room
 * kind ("bedrooms", "bathrooms"). `ok` is the bot's own verdict; the room-map
 * event omits it and the CRM derives the mismatch from the numbers.
 */
export type EchoflicksCountCheck = Record<
  string,
  {
    plan?: number | null;
    listing?: number | null;
    mapped?: number | null;
    /** Placeholder rooms on levels the plan does not show (BLD room map). */
    inferred?: number | null;
    ok?: boolean | null;
  }
>;

export type EchoflicksFloorPlan = {
  version?: string;
  status?: "read" | "unusable" | "failed" | "absent" | string;
  source_url?: string | null;
  source_kind?: "pdf_vector" | "pdf_raster" | "image" | string | null;
  candidates?: Array<{
    url_tail?: string | null;
    kind?: string | null;
    source?: string | null;
    score?: number | null;
    label?: string | null;
  }>;
  pages?: Array<{
    index?: number;
    width?: number;
    height?: number;
    text_layer?: boolean;
    text_labels?: number;
    caption?: string | null;
    /** Plan-set additions (BLD): which source the page came from, the floor
     * title printed on it, and what kind of drawing it is. */
    source_index?: number | null;
    level_hint?: string | null;
    plan_kind?: string | null;
  }>;
  /** S3 keys of the page renders (`input/<user>/floorplan_<hash>_pNN.jpg`),
   * only when status=read and the uploads succeeded. */
  page_keys?: string[];
  page_count_total?: number | null;
  confidence?: number | null;
  model?: string | null;
  timings_ms?: Record<string, number>;
  failure_reason?: string | null;
  rooms?: EchoflicksFloorPlanRoom[];
  features?: EchoflicksFloorPlanFeature[];
  floors?: string[];
  /** `floor_plan.v2` additions (FPV-010/011). A v1 block carries none of
   * them and every reader keeps working off the flat `rooms` list. */
  levels?: EchoflicksFloorPlanLevel[];
  openings?: EchoflicksFloorPlanOpening[];
  property_type?: EchoflicksPropertyType | null;
  count_check?: EchoflicksCountCheck;
  /** Plan-set additions (BLD contract §2.1). Absent on pre-BLD blocks. */
  sources?: EchoflicksFloorPlanSource[];
  buildings?: EchoflicksFloorPlanBuilding[];
  vertical_links?: Array<{
    from?: string | null;
    to?: string | null;
    kind?: string | null;
    from_room?: string | null;
    to_room?: string | null;
  }>;
  coverage?: EchoflicksPlanCoverage | null;
  /** Not in the contract today; read if the bot ever nests the profile here. */
  building_profile?: EchoflicksBuildingProfile | null;
  orientation?: {
    source?: "north_arrow" | "listing_text" | string | null;
    facing?: string | null;
    north_points?: string | null;
    note?: string | null;
  } | null;
};

/** The room a photo was mapped to (orchestrator `media.labeled` → MEDIA row). */
export type EchoflicksMediaRoom = {
  room_id?: string | null;
  name?: string | null;
  category?: string | null;
  confidence?: number | null;
  /** Level the room sits on (`floor_plan.v2`, FPV-031). Absent on v1 echoes;
   * the label is what the plan printed ("Planta baja"). */
  level_id?: string | null;
  level_label?: string | null;
};

/**
 * Payload of the orchestrator's `director.skill_room_map` dashboard event
 * (`room_map.v1`): the photo→room assignment the director skill produced
 * from the floor plan, plus its own diagnostics.
 */
export type RoomMapEventPayload = {
  version?: string;
  source?: "floor_plan_llm" | "floor_plan_llm_fallback" | string;
  model?: string | null;
  elapsed_ms?: number | null;
  attempts?: string[];
  photos?: number | null;
  assigned?: number | null;
  unassigned?: number | null;
  exterior?: number | null;
  rooms_with_photos?: number | null;
  rooms_total?: number | null;
  count_check?: EchoflicksCountCheck;
  diagnostics?: Record<string, number>;
  photo_rooms?: Record<
    string,
    {
      room_id?: string | null;
      name?: string | null;
      confidence?: number | null;
      evidence?: string | null;
      /** Level the assigned room sits on (`floor_plan.v2`, FPV-021). */
      level_id?: string | null;
    }
  >;
  remaining_problems?: string[];
  // --- floor_plan.v2 additions (FPV-021/022) --------------------------------
  // All optional: a v1 room map carries none of them and the card renders
  // exactly as before.
  property_type?: EchoflicksPropertyType | null;
  /** The plan's levels, with how many rooms and photos landed on each. BLD
   * adds kind, storey, building and `source: "inferred"` for a level the
   * plan does not show. */
  levels?: Array<{
    level_id?: string | null;
    label?: string | null;
    ordinal?: number | null;
    photos?: number | null;
    rooms?: number | null;
    kind?: string | null;
    storey?: number | null;
    building_id?: string | null;
    source?: "plan" | "inferred" | string | null;
  }>;
  // --- BLD additions (contract §4), all optional ---------------------------
  /** Subset: structure, subtype, label, storeys, unit_floor, band, coverage. */
  building_profile?: EchoflicksBuildingProfile | null;
  coverage?: EchoflicksPlanCoverage | null;
  /** Level id → photo count, the orchestrator's own tally. */
  levels_with_photos?: Record<string, number>;
  /** Room id → up to 8 furnishing nouns read off the placed photos' captions. */
  room_furnishings?: Record<string, string[]>;
};

export type EchoflicksSessionMedia = {
  source_id: string;
  session_id?: Identifier | null;
  contact_id?: Identifier | null;
  whatsapp_user_id: string;
  kind: string;
  /** WhatsApp media id when preserved by the ingest projection. */
  media_id?: string | null;
  // Per-scene role for generated assets: map_clip | drone_shot | i2v | title_card.
  asset_role?: string | null;
  // Listing-ingest observability record (kind === "ingest_extraction" rows).
  ingest?: EchoflicksListingIngest | null;
  bucket?: string | null;
  s3_key?: string | null;
  local_path?: string | null;
  url?: string | null;
  storage_bucket?: string | null;
  storage_path?: string | null;
  storage_is_public?: boolean | null;
  poc_collection?: string | null;
  poc_included?: boolean | null;
  mime_type?: string | null;
  size_bytes?: number | null;
  created_at_source?: string | null;
  scene_id?: string | number | null;
  i2v_prompt_audit_id?: string | null;
  prompt_channel?: string | null;
  route_contract_version?: string | null;
  route_contract_digest?: string | null;
  provider?: string | null;
  concrete_provider?: string | null;
  model?: string | null;
  model_version?: string | null;
  task_id?: string | null;
  positive_prompt_sha256?: string | null;
  negative_prompt_sha256?: string | null;
  motion_style_enabled?: boolean | null;
  motion_style_applied?: boolean | null;
  motion_style_suppressed_reason?: string | null;
  face_sensitive?: boolean | null;
  face_sensitive_reason?: string | null;
  semantic_enforcement_mode?: string | null;
  expression_qc_mode?: string | null;
  i2v_prompt_audit_summary?: I2VPromptAuditSummary | null;
  // Gemini image labels (media.labeled event) + derived orientation, for the
  // "what Gemini saw" view. Absent on pre-feature echoes.
  gemini_caption?: string | null;
  gemini_description?: string | null;
  // Room the director's room map assigned this photo to (FPR-022; copied
  // from `media.labeled` next to the caption). Absent on pre-feature echoes.
  room?: EchoflicksMediaRoom | null;
  orientation?: string | null; // portrait | landscape | square
  width?: number | null;
  height?: number | null;
  raw_metadata?: Record<string, unknown>;
} & Pick<RaRecord, "id">;

export type ImageTriageState =
  | "complete"
  | "partial_analyzer"
  | "partial_projection"
  | "pending"
  | "no_images"
  | "unavailable";

export type ImageTriageFinding = {
  code: string;
  label: string;
  severity: string;
  confidence_band: string;
  detector_version: string;
  evidence: Record<string, string | number | boolean | null>;
};

export type ImageTriageMembership = {
  group_id: string;
  group_kind?: string | null;
  display_anchor?: string | null;
  member_count?: number | null;
  confidence_band?: string | null;
};

export type ImageTriageObservation = {
  occurrence_id: string;
  source: {
    occurrence_id: string;
    source_key: string;
    source_key_sha256?: string | null;
    upload_sequence: number;
    mime_type?: string | null;
  };
  status: string;
  reason_code?: string | null;
  features?: Record<string, string | number | boolean | null> | null;
  findings: ImageTriageFinding[];
  memberships: ImageTriageMembership[];
  detectors: Array<Record<string, unknown>>;
  media?: {
    id?: Identifier | null;
    bucket?: string | null;
    s3_key?: string | null;
    kind?: string | null;
    mime_type?: string | null;
  } | null;
  media_match: "exact_key" | "unmatched" | string;
};

export type ImageTriageGroup = {
  group_id: string;
  group_kind?: string | null;
  display_anchor?: string | null;
  member_count?: number | null;
  confidence_band?: string | null;
  members: string[];
  projection_complete: boolean;
};

export type ImageTriageDto = {
  schema_version: "image-triage-api.v1";
  session_id: string;
  state: ImageTriageState;
  status_copy: string;
  observe_only: boolean;
  suppression: {
    mode: string;
    retained_occurrences: number;
    suppressed_occurrences: number;
    suppressed_source_keys?: number | null;
    duplicate_components?: number | null;
    groups_considered?: number | null;
    advisory_groups_ignored?: number | null;
    observed_at?: string | null;
  } | null;
  report: {
    report_id: string;
    report_digest?: string | null;
    source_set_digest?: string | null;
    status?: string | null;
    mode?: string | null;
    completed_at?: string | null;
    config?: {
      release_id?: string | null;
      digest?: string | null;
      schema_version?: number | null;
    } | null;
    analyzer?: {
      bundle_version?: string | null;
      providers?: Record<string, string>;
    } | null;
    source?: {
      kind: "image_triage_report";
      bucket?: string | null;
      s3_key?: string | null;
    } | null;
  } | null;
  summary: {
    source_images?: number;
    analyzed_images?: number;
    failed_images?: number;
    excluded_videos?: number;
    finding_counts?: Record<string, number>;
    group_counts?: Record<string, number>;
    grouped_image_counts?: Record<string, number>;
  } | null;
  projection: {
    expected_observations: number;
    projected_observations: number;
    malformed_observations: number;
    complete: boolean;
  };
  observations: ImageTriageObservation[];
  groups: ImageTriageGroup[];
  unmatched_observations: string[];
};

export type ImageTriageResponse = {
  data: ImageTriageDto;
  meta: {
    total?: number;
    has_more: boolean;
    next_cursor?: string | null;
  };
};

export type I2VPromptAuditSummary = {
  schema_version?: string | null;
  audit_id?: string | null;
  scene_id?: string | number | null;
  source_scene_type?: string | null;
  attempt?: number | null;
  max_attempts?: number | null;
  prompt_channel?: string | null;
  route_contract_version?: string | null;
  route_contract_digest?: string | null;
  provider?: string | null;
  route?: string | null;
  concrete_provider?: string | null;
  model?: string | null;
  model_version?: string | null;
  mode?: string | null;
  task_id?: string | null;
  positive_prompt_sha256?: string | null;
  negative_prompt_sha256?: string | null;
  negative_prompt_source?: string | null;
  scene_prompt_source?: string | null;
  scene_prompt_family?: string | null;
  motion_style_enabled?: boolean | null;
  motion_style_applied?: boolean | null;
  motion_style_suppressed_reason?: string | null;
  face_sensitive?: boolean | null;
  face_sensitive_reason?: string | null;
  matched_subject_terms?: string[];
  matched_expression_terms?: string[];
  generic_face_terms_enabled?: boolean | null;
  semantic_enforcement_mode?: string | null;
  expression_qc_mode?: string | null;
  reference_frames?: Record<string, unknown> | null;
  asset_s3_key?: string | null;
  asset_disposition?: string | null;
  lifecycle_status?: string | null;
  lifecycle_terminal?: boolean | null;
  error_code?: string | null;
  error_message?: string | null;
  retryable?: boolean | null;
  fallback?: boolean | null;
  fallback_from?: string | null;
  authoring_audit_id?: string | null;
  authoring_run_id?: string | null;
  authoring_target_key?: string | null;
  authoring_status?: string | null;
  authoring_correlation?: string | null;
  authoring_prompt_sha256?: string | null;
  authoring_guide_version?: string | null;
  authoring_guide_digest?: string | null;
  authoring_vision_digest?: string | null;
  authoring_shot_list_digest?: string | null;
  route_role?: string | null;
  lock_compliant?: boolean | null;
  scene_revision_id?: string | null;
  packet_id?: string | null;
  packet_digest?: string | null;
  provider_request_digest?: string | null;
  idempotency_key?: string | null;
  semantic_attempt_ordinal?: number | null;
  transport_try_index?: number | null;
  parent_attempt_id?: string | null;
  patch_id?: string | null;
  base_packet_digest?: string | null;
  flow_id?: string | null;
  config_release_id?: string | null;
  config_release_digest?: string | null;
  prompt_bundle_id?: string | null;
  prompt_bundle_digest?: string | null;
  template_id?: string | null;
  template_version?: string | null;
  template_digest?: string | null;
  transformation_records?: Record<string, unknown>[];
  capability_decisions?: Record<string, unknown>[];
  capability_warnings?: Record<string, unknown>[];
  render_spec_fingerprint?: string | null;
  reuse_status?: string | null;
  reuse_reason?: string | null;
  requested_model?: string | null;
  reported_model?: string | null;
  correction_validation?: {
    phase?: string | null;
    status?: string | null;
    reason_code?: string | null;
    error?: string | null;
    submitted?: boolean | null;
    semantic_attempt_ordinal?: number | null;
  } | null;
};

export type I2VPromptAuthoringAttempt = {
  ordinal?: number | null;
  status?: string | null;
  started_at?: string | null;
  elapsed_ms?: number | null;
  semaphore_wait_ms?: number | null;
  requested_model?: string | null;
  observed_model?: string | null;
  observed_provider?: string | null;
  input_tokens?: number | null;
  output_tokens?: number | null;
  usage_source?: string | null;
  error_code?: string | null;
  error_message?: string | null;
  retryable?: boolean | null;
};

export type I2VPromptAuthoring = {
  id: string;
  audit_id: string;
  schema_version?: string | null;
  authoring_run_id?: string | null;
  scene_id?: string | number | null;
  scene_position?: number | null;
  previous_scene_id?: string | number | null;
  next_scene_id?: string | number | null;
  source_file?: string | null;
  source_media_id?: string | null;
  source_storage_key?: string | null;
  source_image_sha256?: string | null;
  source_width?: number | null;
  source_height?: number | null;
  target_key?: string | null;
  target_scope?: string | null;
  requested_model?: string | null;
  route_role?: string | null;
  scene_revision_id?: string | null;
  prompt_revision_id?: string | null;
  config_release_id?: string | null;
  config_release_digest?: string | null;
  route_contract_version?: string | null;
  route_contract_digest?: string | null;
  prompt_bundle_id?: string | null;
  prompt_bundle_digest?: string | null;
  template_id?: string | null;
  template_version?: string | null;
  template_digest?: string | null;
  status?: string | null;
  terminal?: boolean | null;
  prompt?: string | null;
  prompt_sha256?: string | null;
  prompt_truncated?: boolean | null;
  guide_version?: string | null;
  guide_digest?: string | null;
  vision_digest?: string | null;
  shot_list_digest?: string | null;
  decision?: Record<string, unknown> | null;
  attempts?: I2VPromptAuthoringAttempt[];
  lock_compliant?: boolean | null;
  locked_contract_hash?: string | null;
  error_code?: string | null;
  error_message?: string | null;
  redaction_state?: string | null;
  projection_state?: string | null;
  projection_warning?: string | null;
  occurred_at?: string | null;
  updated_at?: string | null;
  projection_conflicts?: string[];
};

export type I2VPromptDirectorSummary = {
  id?: string | null;
  schema_version?: string | null;
  authoring_run_id?: string | null;
  status?: string | null;
  authoring_mode?: string | null;
  scene_count?: number | null;
  images_attached?: number | null;
  attachment_complete?: boolean | null;
  authored_scene_count?: number | null;
  resolved_scene_count?: number | null;
  resolved_outcome_count?: number | null;
  copied_legacy_count?: number | null;
  copied_legacy_scene_count?: number | null;
  authoring_record_count?: number | null;
  terminal_outcome_count?: number | null;
  primary_scene_count?: number | null;
  secondary_scene_count?: number | null;
  target_keys?: string[];
  authored_counts?: Record<string, number> | null;
  missing_images?: Record<string, unknown>[];
  failed_keys?: string[];
  authoring_budget?: Record<string, unknown> | null;
  vision_digest?: string | null;
  shot_list_digest?: string | null;
  route_contract?: Record<string, unknown> | null;
  config_release_id?: string | null;
  config_release_digest?: string | null;
  route_contract_version?: string | null;
  route_contract_digest?: string | null;
  prompt_bundle_id?: string | null;
  prompt_bundle_digest?: string | null;
  template_id?: string | null;
  template_version?: string | null;
  template_digest?: string | null;
  error_code?: string | null;
  occurred_at?: string | null;
  updated_at?: string | null;
};

export type I2VPromptAudit = {
  schema_version?: string | null;
  audit_id?: string | null;
  workload_id?: string | null;
  session_id?: string | null;
  user_id?: string | null;
  scene_id?: string | number | null;
  source_scene_type?: string | null;
  attempt?: number | null;
  max_attempts?: number | null;
  prompt_channel?: string | null;
  route_contract?: Record<string, unknown> | null;
  provider?: Record<string, unknown> | null;
  kling?: Record<string, unknown> | null;
  prompts?: {
    scene_prompt?: string | null;
    source_preservation_prompt?: string | null;
    final_positive_prompt?: string | null;
    negative_prompt?: string | null;
    negative_prompt_source?: string | null;
    positive_prompt_sha256?: string | null;
    negative_prompt_sha256?: string | null;
    prompt_text_truncated?: boolean | null;
    negative_prompt_text_truncated?: boolean | null;
  } | null;
  authoring?: Record<string, unknown> | null;
  qc_context?: Record<string, unknown> | null;
  reference_frames?: Record<string, unknown> | null;
  lifecycle?: Record<string, unknown> | null;
  asset?: Record<string, unknown> | null;
  correction_validation?: I2VPromptAuditSummary["correction_validation"];
};

export type I2VPromptAttempt = {
  id: string;
  audit_id: string;
  schema_version?: string | null;
  session_id?: Identifier | null;
  workload_id?: string | null;
  scene_id?: string | number | null;
  attempt?: number | null;
  max_attempts?: number | null;
  status?: string | null;
  terminal?: boolean | null;
  provider?: string | null;
  concrete_provider?: string | null;
  model?: string | null;
  task_id?: string | null;
  elapsed_ms?: number | null;
  queue_wait_ms?: number | null;
  error_code?: string | null;
  error_message?: string | null;
  retryable?: boolean | null;
  fallback?: boolean | null;
  fallback_from?: string | null;
  asset_s3_key?: string | null;
  resolved_at?: string | null;
  submitted_at?: string | null;
  finished_at?: string | null;
  route_contract_version?: string | null;
  route_contract_digest?: string | null;
  scene_revision_id?: string | null;
  packet_id?: string | null;
  packet_digest?: string | null;
  provider_request_digest?: string | null;
  idempotency_key?: string | null;
  semantic_attempt_ordinal?: number | null;
  transport_try_index?: number | null;
  parent_attempt_id?: string | null;
  patch_id?: string | null;
  base_packet_digest?: string | null;
  flow_id?: string | null;
  config_release_id?: string | null;
  config_release_digest?: string | null;
  prompt_bundle_id?: string | null;
  prompt_bundle_digest?: string | null;
  template_id?: string | null;
  template_version?: string | null;
  template_digest?: string | null;
  transformation_records?: Record<string, unknown>[];
  capability_decisions?: Record<string, unknown>[];
  capability_warnings?: Record<string, unknown>[];
  render_spec_fingerprint?: string | null;
  reuse_status?: string | null;
  reuse_reason?: string | null;
  requested_model?: string | null;
  reported_model?: string | null;
  protected_event_id?: string | null;
  projection_conflicts?: string[];
  prompt_audit_summary?: I2VPromptAuditSummary | null;
};

export type FinalVideoDeliveryReconciliation = {
  event_id?: string | null;
  actor: string;
  reason: string;
  previous_status: "outcome_unknown";
  resolved_status: "delivered" | "failed";
  occurred_at: string;
};

export type FinalVideoDelivery = {
  id: string;
  delivery_id: string;
  workload_id?: string | null;
  asset_identity?: string | null;
  channel?: string | null;
  status?: string | null;
  terminal?: boolean | null;
  method?: string | null;
  provider_message_id?: string | null;
  failure_reason?: string | null;
  reconciliation_required?: boolean | null;
  reconciled_at?: string | null;
  reconciled_by?: string | null;
  reconciliation_reason?: string | null;
  reconciliation_history?: FinalVideoDeliveryReconciliation[];
  duplicate_suppressed_count?: number | null;
  final_asset_s3_key?: string | null;
  occurred_at?: string | null;
  updated_at?: string | null;
  lifecycle_updated_at?: string | null;
  projection_conflicts?: string[];
  outcome?: Record<string, unknown> | null;
};

export type EchoflicksSessionMessage = {
  source_id: string;
  session_id?: Identifier | null;
  contact_id?: Identifier | null;
  whatsapp_user_id: string;
  role: string;
  message_type?: string | null;
  text?: string | null;
  occurred_at?: string | null;
  source: string;
  source_confidence: string;
  raw_payload?: Record<string, unknown>;
  buttons?: WhatsAppMessageButton[] | null;
  button_titles?: string[] | null;
  button_reply?: WhatsAppButtonReply | null;
  /** WhatsApp wamid; absent on rows that never went through WhatsApp. */
  provider_message_id?: string | null;
  /** "web" on a my.echoflicks.com turn (MYC-015): not a WhatsApp message. */
  channel?: string | null;
  // EMC contract C6: outbound rows joined to `WAMSG#{wamid}` and the COST row.
  // All optional: an API that predates EMC sends none of them.
  /** Meta's latest status for the send: sent, delivered, read, failed. */
  provider_status?: string | null;
  /** Meta `pricing.type`: regular, free_customer_service, free_entry_point. */
  pricing_type?: string | null;
  /** Meta `pricing.category`: service, utility, marketing, authentication. */
  pricing_category?: string | null;
  cost_usd?: number | null;
  cost_eur?: number | null;
  /** pending | charged | free | undelivered | failed | unpriced (C3.2). */
  billing_state?: string | null;
  /** The bot's C7 purpose for the send. */
  purpose?: string | null;
} & Pick<RaRecord, "id">;

export type EchoflicksSessionFeedback = {
  source_id: string;
  session_id?: Identifier | null;
  contact_id?: Identifier | null;
  whatsapp_user_id: string;
  rating?: number | null;
  feedback_text?: string | null;
  narrator?: string | null;
  scene_id?: string | number | null;
  video_timestamp_ms?: number | null;
  video_frame?: number | null;
  feedback_category?: string | null;
  occurred_at?: string | null;
  source: string;
  raw_payload?: Record<string, unknown>;
} & Pick<RaRecord, "id">;

export type EchoflicksSessionEvent = {
  source_id: string;
  session_id?: Identifier | null;
  contact_id?: Identifier | null;
  whatsapp_user_id: string;
  event_type: string;
  occurred_at?: string | null;
  source: string;
  raw_payload?: Record<string, unknown>;
} & Pick<RaRecord, "id">;

export type EchoJourneyNodeStatus =
  | "pending"
  | "running"
  | "waiting_for_user"
  | "succeeded"
  | "skipped"
  | "degraded"
  | "failed"
  | "cancelled";

export type EchoJourneyStage =
  | "intake"
  | "conversation"
  | "director"
  | "script"
  | "composition"
  | "delivery"
  | "feedback";

export type EchoJourneyField = {
  kind: string;
  label: string;
  value?: string | number | boolean | null;
  bucket?: string | null;
  s3_key?: string | null;
  summary?: string | null;
  metadata?: Record<string, unknown> | null;
};

export type EchoJourneyTiming = {
  started_at?: string | null;
  completed_at?: string | null;
  elapsed_ms?: number | null;
  latency_ms?: number | null;
  queue_ms?: number | null;
  provider_ms?: number | null;
  upload_ms?: number | null;
  retry_count?: number | null;
};

export type EchoJourneyToolUsage = {
  name: string;
  provider?: string | null;
  category?: string | null;
  model?: string | null;
  action?: string | null;
  operation?: string | null;
  attempt?: number | null;
  status?: string | null;
  elapsed_ms?: number | null;
  latency_ms?: number | null;
  task_id?: string | null;
  metadata?: Record<string, unknown> | null;
};

export type EchoJourneyDurationImpact = {
  scene_id?: string | number | null;
  source_scene_type?: string | null;
  final_scene_type?: string | null;
  route?: string | null;
  provider?: string | null;
  target_seconds?: number | null;
  planned_seconds?: number | null;
  requested_seconds?: number | null;
  measured_seconds?: number | null;
  output_seconds?: number | null;
  planned_scene_seconds?: number | null;
  generated_asset_seconds?: number | null;
  target_video_seconds?: number | null;
  expected_video_seconds?: number | null;
  actual_video_seconds?: number | null;
  moviepy_duration_seconds?: number | null;
  delta_seconds?: number | null;
  delta_frames?: number | null;
  tolerance_frames?: number | null;
  action?: string | null;
  duration_qc_status?: string | null;
  fallback_reason?: string | null;
  s3_key?: string | null;
  reason?: string | null;
};

export type EchoJourneyNode = {
  id: string;
  session_id?: Identifier | null;
  workload_id?: string | null;
  whatsapp_user_id?: string | null;
  type: string;
  stage: EchoJourneyStage | string;
  stage_order?: number;
  label: string;
  status: EchoJourneyNodeStatus | string;
  occurred_at?: string | null;
  completed_at?: string | null;
  service?: string | null;
  decision?: Record<string, unknown> | null;
  i2v_prompt_audit_summary?: I2VPromptAuditSummary | null;
  i2v_prompt_audit?: I2VPromptAudit | null;
  inputs?: EchoJourneyField[];
  outputs?: EchoJourneyField[];
  artifacts?: EchoJourneyField[];
  timing?: EchoJourneyTiming | null;
  tools?: EchoJourneyToolUsage[];
  duration_impact?: EchoJourneyDurationImpact | null;
  debug_notes?: string[];
  bottleneck_score?: number | null;
  raw_payload?: Record<string, unknown> | null;
  confidence?: string | null;
};

export type EchoJourneyEdge = {
  id: string;
  from: string;
  to: string;
  label?: string | null;
  kind?: "normal" | "fallback" | "retry" | "cancel" | "failure" | string;
  confidence?: string | null;
};

export type EchoflicksFramingSubjectSummary = {
  subject_id?: string | null;
  label?: string | null;
  kind?: string | null;
  priority?: string | null;
  confidence?: number | null;
  detector?: string | null;
  coordinate_space_id?: string | null;
  identity_required?: boolean | null;
};

export type EchoflicksFramingQcSummary = {
  status?: string | null;
  issue_codes?: string[];
  metrics?: Record<string, number | string | boolean | null> | null;
  /** Canonical v2 field. */
  checked_coordinate_space_id?: string | null;
  /** Legacy v1 alias retained while old projections age out. */
  checked_space_id?: string | null;
  sample_count?: number | null;
  repair_attempt?: number | null;
};

export type EchoflicksFramingSummary = {
  status?: string | null;
  at_risk?: boolean | null;
  failed_scene_count?: number | null;
  repaired_scene_count?: number | null;
  fallback_scene_count?: number | null;
  tracking_scene_count?: number | null;
  report_storage_bucket?: string | null;
  report_storage_path?: string | null;
  latest_at?: string | null;
};

export type EchoflicksSessionFramingEvent = {
  session_id: Identifier;
  event_type: string;
  scene_id?: string | number | null;
  schema_version?: string | null;
  framing_plan_id?: string | null;
  plan_digest?: string | null;
  plan_revision?: number | null;
  attempt_id?: string | null;
  task_id?: string | null;
  receipt_id?: string | null;
  config_release_id?: string | null;
  config_digest?: string | null;
  config_schema_version?: string | null;
  timeline_start_seconds?: number | null;
  timeline_end_seconds?: number | null;
  intent_source?: string | null;
  strategy?: string | null;
  subject_summary?: EchoflicksFramingSubjectSummary[];
  qc_summary?: EchoflicksFramingQcSummary | null;
  repair_summary?: Record<string, unknown> | null;
  fallback_reason?: string | null;
  provider_aspect?: string | null;
  delivery_aspect?: string | null;
  artifact_refs?: Array<Record<string, unknown>>;
  report_storage_bucket?: string | null;
  report_storage_path?: string | null;
  payload?: Record<string, unknown> | null;
  occurred_at?: string | null;
  source?: string | null;
} & Pick<RaRecord, "id">;

export type EchoflicksSessionJourney = {
  session_id: Identifier;
  workload_id?: string | null;
  summary?: {
    status?: string | null;
    started_at?: string | null;
    completed_at?: string | null;
    render_created_at?: string | null;
    final_video_delivery_status?: string | null;
    final_video_delivery_channel?: string | null;
    final_video_delivery_method?: string | null;
    final_video_delivery_updated_at?: string | null;
    fallback_count?: number;
    failed_node_id?: string | null;
    confidence?: string | null;
    observed_elapsed_ms?: number | null;
    target_video_seconds?: number | null;
    actual_video_seconds?: number | null;
    framing?: EchoflicksFramingSummary | null;
  };
  nodes: EchoJourneyNode[];
  edges: EchoJourneyEdge[];
  i2v_prompt_attempts?: I2VPromptAttempt[];
  i2v_prompt_authorings?: I2VPromptAuthoring[];
  i2v_prompt_director?: I2VPromptDirectorSummary | null;
  final_video_deliveries?: FinalVideoDelivery[];
  i2v_prompt_telemetry_state?:
    | "available"
    | "partial"
    | "unavailable_legacy"
    | "excluded_by_policy"
    | "projection_degraded";
  framing?: EchoflicksSessionFramingEvent[];
} & Pick<RaRecord, "id">;

// Pipeline-timing waterfall (GET /video-sessions/{id}/timings).
// Spans are producer-measured wall-clock stages; gaps are queue/cold-start
// segments the API derives between legs. See docs/timing-spans.md in the
// echoflicks workspace for the span taxonomy.
export type EchoTimingSpan = {
  name: string;
  started_at?: string | null;
  elapsed_ms?: number | null;
  status?: string | null;
  leg?: string | null;
  attrs?: Record<string, unknown> | null;
  /** Parent span name, when the producer records it. */
  parent?: string | null;
  /** `orch.llm.summary` only: count / sum / max rows per (role, route). */
  groups?: Array<Record<string, unknown>> | null;
};

export type EchoTimingLeg = {
  /** bot_initiate | orchestrator | composer_generate | composer | bot_delivery */
  leg: string;
  leg_started_at?: string | null;
  leg_elapsed_ms?: number | null;
  initiated_at?: string | null;
  outcome?: string | null;
  skill?: string | null;
  span_count?: number | null;
  spans_dropped?: number | null;
  spans: EchoTimingSpan[];
};

export type EchoTimingGap = {
  name: string;
  seconds: number;
  from?: string | null;
  to?: string | null;
  prewarm_hit?: boolean;
  clock_suspect?: boolean;
};

export type EchoflicksSessionTimings = {
  session_id?: Identifier | null;
  workload_id?: string | null;
  t0?: string | null;
  /** Legs in the API's `_TIMING_LEG_ORDER`, each carrying its own spans. */
  legs: EchoTimingLeg[];
  /** Flat copy of every leg's spans (older APIs); the UI reads `legs[].spans`. */
  spans: EchoTimingSpan[];
  gaps: EchoTimingGap[];
  totals: {
    e2e_seconds?: number | null;
    e2e_to_render_seconds?: number | null;
  };
  /** True when the API stopped paging before it read every event (ECP-001). */
  truncated?: boolean | null;
  /** Event pages the API read. */
  pages?: number | null;
} & Pick<RaRecord, "id">;

export type ContactNote = {
  contact_id: Identifier;
  text: string;
  date: string;
  sales_id: Identifier;
  status: string;
  attachments?: AttachmentNote[];
} & Pick<RaRecord, "id">;

export type Deal = {
  name: string;
  company_id: Identifier;
  contact_ids: Identifier[];
  category: string;
  stage: string;
  description: string;
  amount: number;
  created_at: string;
  updated_at: string;
  archived_at?: string;
  expected_closing_date: string;
  sales_id: Identifier;
  index: number;
} & Pick<RaRecord, "id">;

export type DealNote = {
  deal_id: Identifier;
  text: string;
  date: string;
  sales_id: Identifier;
  attachments?: AttachmentNote[];

  // This is defined for compatibility with `ContactNote`
  status?: undefined;
} & Pick<RaRecord, "id">;

export type Tag = {
  id: number;
  name: string;
  color: string;
};

export type Task = {
  contact_id: Identifier;
  type: string;
  text: string;
  due_date: string;
  done_date?: string | null;
  sales_id?: Identifier;
} & Pick<RaRecord, "id">;

export type ActivityCompanyCreated = {
  type: typeof COMPANY_CREATED;
  company_id: Identifier;
  company: Company;
  sales_id: Identifier;
  date: string;
} & Pick<RaRecord, "id">;

export type ActivityContactCreated = {
  type: typeof CONTACT_CREATED;
  company_id: Identifier;
  sales_id?: Identifier;
  contact: Contact;
  date: string;
} & Pick<RaRecord, "id">;

export type ActivityContactNoteCreated = {
  type: typeof CONTACT_NOTE_CREATED;
  sales_id?: Identifier;
  contactNote: ContactNote;
  date: string;
} & Pick<RaRecord, "id">;

export type ActivityDealCreated = {
  type: typeof DEAL_CREATED;
  company_id: Identifier;
  sales_id?: Identifier;
  deal: Deal;
  date: string;
};

export type ActivityDealNoteCreated = {
  type: typeof DEAL_NOTE_CREATED;
  sales_id?: Identifier;
  dealNote: DealNote;
  date: string;
};

export type Activity = RaRecord &
  (
    | ActivityCompanyCreated
    | ActivityContactCreated
    | ActivityContactNoteCreated
    | ActivityDealCreated
    | ActivityDealNoteCreated
  );

export interface RAFile {
  src: string;
  title: string;
  path?: string;
  rawFile: File;
  type?: string;
}

export type AttachmentNote = RAFile;

export interface LabeledValue {
  value: string;
  label: string;
}

export type DealStage = LabeledValue;

export interface NoteStatus extends LabeledValue {
  color: string;
}

export interface ContactGender {
  value: string;
  label: string;
  icon: ComponentType<{ className?: string }>;
}
